/**
 * El directorio en nueve idiomas.
 *
 * Cada idioma tiene SU PROPIA direccion, y esa es toda la razon de que este
 * fichero exista: el selector de idioma del resto del sitio guarda una cookie y
 * sirve contenido distinto en la misma direccion, y eso un buscador no lo puede
 * indexar. Aqui el idioma va en la URL, cada pagina declara sus hermanas con
 * hreflang, y el castellano se queda sin prefijo por ser el de casa.
 *
 * Las frases viven en codigo y no en la base de datos a proposito: asi el
 * compilador avisa si falta una. Lo que si esta en la base son los nombres y
 * los slugs de las categorias, porque son datos que se siembran.
 *
 * Casi todo son funciones en vez de plantillas con huecos porque el orden de
 * las palabras cambia: "Bares y restaurantes en Palma" es "Bars und Restaurants
 * in Palma" pero tambien "Бары и рестораны в Пальме".
 */

export const IDIOMAS = ["es", "de", "en", "ca", "fr", "it", "nl", "pl", "ru"] as const;
export type Idioma = (typeof IDIOMAS)[number];

/** El de casa. Va sin prefijo en la URL y es el x-default. */
export const IDIOMA_POR_DEFECTO: Idioma = "es";

/** Los que llevan prefijo: todos menos el de casa. */
export const IDIOMAS_CON_PREFIJO = IDIOMAS.filter((i) => i !== IDIOMA_POR_DEFECTO);

/**
 * El trozo de URL de la seccion. Traducido en todos los idiomas, castellano
 * incluido: nadie busca "directorio", todo el mundo busca "restaurantes".
 */
export const SECCION: Record<Idioma, string> = {
  es: "restaurantes",
  de: "restaurants",
  en: "restaurants",
  ca: "restaurants",
  fr: "restaurants",
  it: "ristoranti",
  nl: "restaurants",
  pl: "restauracje",
  ru: "restorany",
};

/**
 * El trozo de URL de la landing de captacion.
 *
 * No puede coincidir con SECCION, y ademas es mejor asi: un hostelero busca
 * "carta digital gratis", no "para restaurantes". La direccion trabaja a favor
 * en vez de ser un cajon de sastre.
 */
export const LANDING: Record<Idioma, string> = {
  es: "carta-digital",
  de: "digitale-speisekarte",
  en: "digital-menu",
  ca: "carta-digital",
  fr: "carte-numerique",
  it: "menu-digitale",
  nl: "digitale-menukaart",
  pl: "cyfrowe-menu",
  ru: "tsifrovoe-menyu",
};

/** El trozo de URL del buscador, dentro de la seccion. */
export const BUSCAR: Record<Idioma, string> = {
  es: "buscar",
  de: "suche",
  en: "search",
  ca: "cercar",
  fr: "recherche",
  it: "cerca",
  nl: "zoeken",
  pl: "szukaj",
  ru: "poisk",
};

/** Como se llama cada idioma en su propio idioma, para el selector. */
export const NOMBRE_IDIOMA: Record<Idioma, string> = {
  es: "Español",
  de: "Deutsch",
  en: "English",
  ca: "Català",
  fr: "Français",
  it: "Italiano",
  nl: "Nederlands",
  pl: "Polski",
  ru: "Русский",
};

/** El codigo que va en hreflang y en el atributo lang del html. */
export const HREFLANG: Record<Idioma, string> = {
  es: "es-ES",
  de: "de",
  en: "en",
  ca: "ca",
  fr: "fr",
  it: "it",
  nl: "nl",
  pl: "pl",
  ru: "ru",
};

export function esIdioma(valor: string): valor is Idioma {
  return (IDIOMAS as readonly string[]).includes(valor);
}

/**
 * La direccion de una pagina del directorio en un idioma.
 * rutaDirectorio("de", "mallorca", "palma") -> /de/restaurants/mallorca/palma
 * rutaDirectorio("es", "mallorca")          -> /restaurantes/mallorca
 */
export function rutaDirectorio(idioma: Idioma, ...trozos: (string | null | undefined)[]) {
  const cola = trozos.filter(Boolean).join("/");
  const raiz =
    idioma === IDIOMA_POR_DEFECTO ? `/${SECCION[idioma]}` : `/${idioma}/${SECCION[idioma]}`;
  return cola ? `${raiz}/${cola}` : raiz;
}

/** La landing de captacion, que tambien va traducida. */
export function rutaRestaurantes(idioma: Idioma) {
  return idioma === IDIOMA_POR_DEFECTO
    ? `/${LANDING[idioma]}`
    : `/${idioma}/${LANDING[idioma]}`;
}

/**
 * El alta de "mi negocio no esta".
 *
 * Ruta unica y sin traducir, con el idioma en la consulta, igual que
 * reclamar: no se indexa, asi que nueve URLs solo servirian para repartir
 * entre nueve paginas la autoridad de un formulario que nadie busca en Google.
 */
export function rutaAlta(idioma: Idioma) {
  return idioma === IDIOMA_POR_DEFECTO
    ? "/restaurantes/alta"
    : `/restaurantes/alta?lang=${idioma}`;
}

export const BASE_URL = "https://zindra.com";

/**
 * Los `alternates` de una pagina: la misma pagina en los demas idiomas.
 *
 * Sin esto, las nueve versiones compiten entre ellas como si fueran nueve
 * paginas distintas que dicen casi lo mismo. Con esto, el buscador sabe que son
 * la misma y le ensena a cada uno la suya.
 */
export function alternativas(construir: (idioma: Idioma) => string) {
  const languages: Record<string, string> = {};
  for (const idioma of IDIOMAS) {
    languages[HREFLANG[idioma]] = `${BASE_URL}${construir(idioma)}`;
  }
  languages["x-default"] = `${BASE_URL}${construir(IDIOMA_POR_DEFECTO)}`;
  return languages;
}

export type DatosDePreguntas = {
  municipio: string;
  isla: string;
  total: number;
  conCarta: number;
  categorias: { nombre: string; cuantos: number }[];
};

export type Pregunta = { pregunta: string; respuesta: string };

export type Textos = {
  directorio: string;
  /** "Guía de restaurantes de Mallorca": el nombre de la guía de una isla. */
  guiaDe: (isla: string) => string;
  guiaIntro: (total: string, municipios: number) => string;
  dondeComerEn: (lugar: string) => string;
  todosLosMunicipios: string;
  queTeApetece: string;
  queTeApeteceEn: (lugar: string) => string;
  todosLosTipos: string;
  masOpciones: string;
  elegirMunicipio: string;
  masPopularesEn: (lugar: string) => string;
  masPopularesNota: string;
  planes: string;
  verTodos: string;
  buscar: string;
  buscarTitulo: string;
  buscarPlaceholder: string;
  buscarBoton: string;
  buscarSinResultados: string;
  buscarDaloDeAlta: string;
  municipios: string;
  negocios: string;
  porTipoDeNegocio: string;
  porCaracteristica: string;
  porMunicipio: string;
  portadaTitulo: string;
  portadaIntro: string;
  portadaCtaTitulo: string;
  portadaCtaTexto: string;
  ctaBoton: string;
  altaTitulo: string;
  altaTexto: string;
  altaBoton: string;
  ctaMunicipioTitulo: string;
  ctaMunicipioTexto: string;
  ctaIslaTitulo: (isla: string) => string;
  ctaIslaTexto: string;
  ctaCategoriaTitulo: (categoria: string, isla: string) => string;
  baresYRestaurantesEn: (lugar: string) => string;
  islaIntro: (total: string, municipios: number) => string;
  municipioIntro: (total: string, municipio: string, isla: string, conCarta: number) => string;
  categoriaEn: (categoria: string, lugar: string) => string;
  categoriaIntro: (total: string, categoria: string, lugar: string) => string;
  descripcionMunicipio: (total: number, municipio: string, isla: string) => string;
  descripcionCategoria: (total: number, categoria: string, lugar: string) => string;
  descripcionIsla: (isla: string) => string;
  descripcionFicha: (nombre: string, tipo: string, municipio: string, direccion: string | null) => string;
  negociosCuenta: (n: string) => string;
  paginaXdeY: (x: number, y: number) => string;
  anterior: string;
  cerrar: string;
  siguiente: string;
  paginas: string;
  ruta: string;
  verTodosEn: (municipio: string) => string;
  negocioDeHosteleria: string;
  tipoEnLugar: (tipo: string, municipio: string, isla: string) => string;
  datosDeContacto: string;
  horario: string;
  verEnElMapa: string;
  mapaCompleto: string;
  comoLlegar: string;
  activarMapa: string;
  otrosNegociosEn: (municipio: string) => string;
  sinCartaTitulo: string;
  sinCartaReclamada: string;
  sinCartaLibre: string;
  reclamarFicha: string;
  esTuNegocio: string;
  verCartaCompleta: string;
  cartaEtiqueta: string;
  abiertoAhora: string;
  abiertosAhora: string;
  verTodosLosNegocios: string;
  ningunoAbierto: string;
  cerradoAhora: string;
  cerrado: string;
  abierto24h: string;
  dias: string[];
  traducidoAutomaticamente: string;
  servicios: string;
  formasDePago: string;
  precio: string;
  precioMedio: string;
  precioDelPlato: (min: string, max: string) => string;
  platosEnLaCarta: (n: number) => string;
  cartaEn: string;
  reservarMesa: string;
  pedirEnMesa: string;
  enRedes: string;
  cartaEnOtraWeb: string;
  datoVerificado: (fecha: string) => string;
  valoraciones: string;
  popularidad: string;
  michelin: (n: number) => string;
  popularAlta: string;
  popularMedia: string;
  popularBaja: string;
  segunPlataforma: (n: number) => string;
  opinionesTitulo: string;
  opinionesSubtitulo: string;
  sinOpiniones: string;
  fotosDeGoogle: string;
  recomendados: string;
  faq: {
    titulo: string;
    cocina: (n: string) => string;
    precio: (n: string) => string;
    terraza: (n: string) => string;
    accesible: (n: string) => string;
    llevar: (n: string) => string;
    tarjeta: (n: string) => string;
    siTerraza: string;
    siAccesible: string;
    siLlevar: string;
    siTarjeta: string;
  };
  recomendadosCta: string;
  recomendadosPlan: string;
  opinionesEnGoogle: string;
  leerEnGoogle: string;
  verTodasEnGoogle: string;
  resenasDeGoogleAviso: string;
  /** Zindra IA: los pros y contras resumidos de las opiniones. */
  iaPros: string;
  iaContras: string;
  /**
   * El titulo de la seccion. Lleva el nombre del local y la palabra que se
   * busca en cada idioma («Kapadokya opiniones», «Kapadokya reviews»): es la
   * busqueda que esta seccion puede ganar.
   */
  /** El boton de la ficha que llama al local para reservar. */
  reservar: string;
  reservarLlamando: (nombre: string) => string;
  /** El boton que busca el local en Glovo, Uber Eats y Just Eat. */
  pedirParaLlevar: string;
  /** La ultima opcion del desplegable de pedir: llamar al local. */
  llamarParaPedir: string;
  /** El desplegable de pedir cuando ninguna plataforma trabaja en el municipio. */
  sinPedirParaLlevar: (municipio: string) => string;
  iaTitulo: (nombre: string) => string;
  iaSegun: (opiniones: number) => string;
  iaAviso: string;
  iaActualizado: (mes: string) => string;
  precioMuyBarato: string;
  precioModerado: string;
  precioCaro: string;
  precioBastanteCaro: string;
  precioPorPersona: (rango: string) => string;
  segunGoogle: string;
  escribirOpinion: string;
  respuestaDelNegocio: string;
  /** Rotulo de la respuesta del dueño a una opinion de Google: se escribio aqui, no en Google. */
  respuestaEscritaEnZindra: string;
  opinionEnviada: string;
  opinionEnRevision: string;
  campoValoracion: string;
  campoTitulo: string;
  campoOpinion: string;
  publicarOpinion: string;
  avisoOsmAntes: string;
  avisoOsmDespues: string;
  sobreComerEn: (municipio: string) => string;
  preguntas: (datos: DatosDePreguntas) => Pregunta[];
};

/** Une una lista con la conjuncion del idioma: "a, b y c". */
function unir(partes: string[], y: string) {
  if (partes.length <= 1) return partes.join("");
  return `${partes.slice(0, -1).join(", ")} ${y} ${partes[partes.length - 1]}`;
}

const es: Textos = {
  directorio: "Guía de restaurantes",
  guiaDe: (isla) => `Guía de restaurantes de ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} bares y restaurantes en ${municipios} municipios, con su horario, su carta cuando la tienen y lo que opina quien ya ha ido.`,
  dondeComerEn: (lugar) => `Dónde comer en ${lugar}`,
  todosLosMunicipios: "Todos los municipios",
  queTeApetece: "¿Qué te apetece?",
  queTeApeteceEn: (lugar) => `¿Qué te apetece en ${lugar}?`,
  todosLosTipos: "Todos los tipos de cocina y de local",
  masOpciones: "Más tipos y servicios",
  elegirMunicipio: "Elige municipio",
  masPopularesEn: (lugar) => `Los más populares de ${lugar}`,
  masPopularesNota: "Ordenados por el número de opiniones que acumulan.",
  planes: "Según el plan",
  verTodos: "Ver todos",
  buscar: "Buscar",
  buscarTitulo: "Buscar en la guía",
  buscarPlaceholder: "Busca un bar, un restaurante o un municipio",
  buscarBoton: "Buscar",
  buscarSinResultados: "No hay ningún negocio que se llame así.",
  buscarDaloDeAlta: "Si es el tuyo, puedes darlo de alta gratis.",
  municipios: "Municipios",
  negocios: "Negocios",
  porTipoDeNegocio: "Por tipo de negocio",
  porCaracteristica: "Por lo que ofrece",
  porMunicipio: "Por municipio",
  portadaTitulo: "Guía de bares y restaurantes",
  portadaIntro:
    "Los negocios de hostelería de cada localidad, con su dirección, su horario y, cuando la tienen publicada, su carta con precios.",
  portadaCtaTitulo: "¿Tienes un bar o un restaurante?",
  portadaCtaTexto:
    "Crea tu carta digital gratis y publícala aquí. Tu negocio aparece en la guía de tu localidad con tus platos y tus precios, y tus clientes pueden verla antes de entrar.",
  ctaBoton: "Publicar mi carta gratis",
  altaTitulo: "¿Tu negocio no está?",
  altaTexto:
    "Créalo gratis: lo revisamos, lo publicamos en la guía y desde ahí puedes subir tu carta.",
  altaBoton: "Dar de alta mi negocio",
  ctaMunicipioTitulo: "¿Falta tu negocio o quieres publicar tu carta?",
  ctaMunicipioTexto:
    "Crea tu carta digital gratis con Zindra y aparecerá en esta página, con tus platos, tus precios y tu horario al día.",
  ctaIslaTitulo: (isla) => `¿Tu negocio está en ${isla}?`,
  ctaIslaTexto:
    "Publica tu carta gratis y aparece en la guía de tu municipio con tus platos y tus precios.",
  ctaCategoriaTitulo: (categoria, isla) => `¿Tienes ${categoria} en ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Bares y restaurantes en ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} negocios de hostelería repartidos en ${municipios} municipios. Elige el tuyo para ver los bares, restaurantes y cafeterías de la zona con su dirección y su carta.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} negocios de hostelería en ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Los que tienen la carta publicada la muestran con sus platos y sus precios." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} en ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} ${categoria} en ${lugar}, con dirección, horario y, cuando la tienen publicada, su carta con precios.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} bares, restaurantes y cafeterías en ${municipio} (${isla}): dirección, horario, teléfono y carta con precios.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} ${categoria} en ${lugar}: dirección, horario, teléfono y carta con precios.`,
  descripcionIsla: (isla) =>
    `Guía de bares, restaurantes y cafeterías de ${isla} por municipio, con dirección, horario y carta.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} en ${municipio}${direccion ? ` (${direccion})` : ""}. Horario, teléfono, ubicación y carta.`,
  negociosCuenta: (n) => `${n} negocios`,
  paginaXdeY: (x, y) => `Negocios (página ${x} de ${y})`,
  anterior: "Anterior",
  cerrar: "Cerrar",
  siguiente: "Siguiente",
  paginas: "Páginas",
  ruta: "Ruta",
  verTodosEn: (municipio) => `Ver todos los bares y restaurantes de ${municipio}`,
  negocioDeHosteleria: "Negocio de hostelería",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} en ${municipio}, ${isla}`,
  datosDeContacto: "Datos de contacto",
  horario: "Horario",
  verEnElMapa: "Ver en el mapa",
  mapaCompleto: "Ver el mapa completo",
  comoLlegar: "Cómo llegar",
  activarMapa: "Pulsa para mover el mapa",
  otrosNegociosEn: (municipio) => `Otros negocios en ${municipio}`,
  sinCartaTitulo: "Este negocio todavía no ha publicado su carta",
  sinCartaReclamada: "Su carta aparecerá aquí en cuanto la publique.",
  sinCartaLibre:
    "Si es tu negocio, puedes reclamar la ficha y publicar tu carta gratis: tus platos y tus precios saldrán en esta misma página.",
  reclamarFicha: "Reclamar esta ficha",
  esTuNegocio:
    "¿Es tu negocio? Reclama la ficha para corregir los datos y publicar tu carta gratis.",
  verCartaCompleta: "Ver la carta completa",
  cartaEtiqueta: "Carta",
  abiertoAhora: "Abierto ahora",
  abiertosAhora: "Abiertos ahora",
  verTodosLosNegocios: "Ver todos",
  ningunoAbierto: "Ahora mismo no hay ninguno abierto por aquí.",
  cerradoAhora: "Cerrado ahora",
  cerrado: "Cerrado",
  abierto24h: "Abierto 24 h",
  dias: ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"],
  traducidoAutomaticamente: "Traducido automáticamente",
  servicios: "Servicios",
  formasDePago: "Formas de pago",
  precio: "Precio",
  precioMedio: "Precio medio del plato",
  precioDelPlato: (min, max) => `De ${min} a ${max}`,
  platosEnLaCarta: (n) => `${n} platos en la carta`,
  cartaEn: "Carta en",
  reservarMesa: "Se puede reservar mesa",
  pedirEnMesa: "Se puede pedir desde la mesa",
  enRedes: "En redes",
  cartaEnOtraWeb: "Su carta en otra web",
  datoVerificado: (fecha) => `Datos verificados en ${fecha}`,
  valoraciones: "Valoraciones",
  popularidad: "Popularidad",
  michelin: (n) => `${n} ${n === 1 ? "estrella" : "estrellas"} Michelin`,
  popularAlta: "Muy visitado",
  popularMedia: "Bastante visitado",
  popularBaja: "Poco visitado",
  segunPlataforma: (n) => `${n} valoraciones`,
  opinionesTitulo: "¿Qué te ha parecido?",
  opinionesSubtitulo: "Valora tu experiencia",
  sinOpiniones: "Todavía no hay opiniones de este sitio.",
  fotosDeGoogle: "Fotos de Google",
  recomendados: "Recomendados",
  faq: {
    titulo: "Preguntas frecuentes",
    cocina: (n: string) => `¿Qué tipo de cocina hace ${n}?`,
    precio: (n: string) => `¿Cuánto cuesta comer en ${n}?`,
    terraza: (n: string) => `¿${n} tiene terraza?`,
    accesible: (n: string) => `¿${n} es accesible para sillas de ruedas?`,
    llevar: (n: string) => `¿Se puede pedir para llevar en ${n}?`,
    tarjeta: (n: string) => `¿Se puede pagar con tarjeta en ${n}?`,
    siTerraza: "Sí, tiene terraza.",
    siAccesible: "Sí, la entrada es accesible.",
    siLlevar: "Sí, se puede pedir para llevar.",
    siTarjeta: "Sí, aceptan tarjeta.",
  },
  recomendadosCta: "¿Quieres que tu negocio aparezca en Recomendados?",
  recomendadosPlan: "Suscríbete al plan Professional",
  opinionesEnGoogle: "Opiniones en Google",
  leerEnGoogle: "Leer en Google",
  verTodasEnGoogle: "Ver todas en Google Maps",
  resenasDeGoogleAviso: "Opiniones publicadas en Google por sus autores. No son opiniones de Zindra ni se mezclan con ellas.",
  iaPros: "Pros",
  iaContras: "Contras",
  reservar: "Reservar",
  reservarLlamando: (nombre) => `Llamar a ${nombre} para reservar`,
  pedirParaLlevar: "Pedir para llevar",
  llamarParaPedir: "Llamar al local",
  sinPedirParaLlevar: (municipio) => `En ${municipio} no hay pedidos para llevar por Glovo, Uber Eats ni Just Eat`,
  iaTitulo: (nombre) => `Opiniones de ${nombre}: pros y contras`,
  iaSegun: (n) => `Resumen con IA de ${numero("es", n)} opiniones de clientes`,
  iaAviso: "Resumen hecho con inteligencia artificial a partir de las opiniones publicadas en Google y en Zindra. Puede contener errores: las opiniones están más abajo.",
  iaActualizado: (mes) => `Actualizado en ${mes}`,
  precioMuyBarato: "Muy barato",
  precioModerado: "Moderado",
  precioCaro: "Caro",
  precioBastanteCaro: "Bastante caro",
  precioPorPersona: (rango) => `${rango} por persona`,
  segunGoogle: "según Google",
  escribirOpinion: "Escribir una opinión",
  respuestaDelNegocio: "Respuesta del negocio",
  respuestaEscritaEnZindra: "Respuesta del negocio, escrita en Zindra (no en Google)",
  opinionEnviada: "Gracias por tu opinión",
  opinionEnRevision: "La revisamos y se publica en menos de 24 horas.",
  campoValoracion: "Tu valoración",
  campoTitulo: "Título",
  campoOpinion: "Tu opinión",
  publicarOpinion: "Publicar",
  avisoOsmAntes: "Parte de los datos de contacto y ubicación proceden de ",
  avisoOsmDespues:
    ", bajo licencia ODbL. Si algo no está bien, el propio negocio puede corregirlo al reclamar su ficha.",
  sobreComerEn: (municipio) => `Sobre comer en ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `¿Cuántos bares y restaurantes hay en ${municipio}?`,
        respuesta: `En la guía de Zindra hay ${total.toLocaleString("es-ES")} negocios de hostelería en ${municipio} (${isla}), entre bares, restaurantes, cafeterías y comida para llevar. Cada uno tiene su ficha con dirección, teléfono y horario.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `¿Qué tipos de restaurante hay en ${municipio}?`,
        respuesta: `Los más numerosos en ${municipio} son ${unir(categorias.map((c) => `${c.cuantos} ${c.nombre}`), "y")}. Cada tipo tiene su propia página dentro de la guía.`,
      });
    }
    preguntas.push({
      pregunta: "¿Se puede ver la carta antes de ir?",
      respuesta:
        conCarta > 0
          ? `Sí. ${conCarta === 1 ? "Un negocio" : `${conCarta} negocios`} de ${municipio} ${conCarta === 1 ? "tiene" : "tienen"} su carta publicada, con los platos y los precios, y se ve desde el móvil sin instalar nada.`
          : `Todavía no hay ningún negocio de ${municipio} con la carta publicada. Los que la publiquen mostrarán sus platos y sus precios en su ficha.`,
    });
    preguntas.push({
      pregunta: `Tengo un bar en ${municipio}, ¿cómo salgo aquí?`,
      respuesta: `Creando tu carta digital en Zindra, que es gratis, y marcándola para que se vea en la guía. Si tu negocio ya aparece en la lista, puedes reclamar su ficha desde ella. Cuanto más completa esté, más arriba sale en esta página.`,
    });
    return preguntas;
  },
};

const de: Textos = {
  directorio: "Restaurantführer",
  guiaDe: (isla) => `Restaurantführer ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} Bars und Restaurants in ${municipios} Gemeinden, mit Öffnungszeiten, Speisekarte, wo es eine gibt, und dem, was Gäste darüber sagen.`,
  dondeComerEn: (lugar) => `Wo man auf ${lugar} gut isst`,
  todosLosMunicipios: "Alle Gemeinden",
  queTeApetece: "Worauf haben Sie Lust?",
  queTeApeteceEn: (lugar) => `Worauf haben Sie Lust in ${lugar}?`,
  todosLosTipos: "Alle Küchen und Lokalarten",
  masOpciones: "Weitere Arten und Angebote",
  elegirMunicipio: "Gemeinde wählen",
  masPopularesEn: (lugar) => `Die beliebtesten Lokale auf ${lugar}`,
  masPopularesNota: "Sortiert nach der Zahl ihrer Bewertungen.",
  planes: "Je nach Anlass",
  verTodos: "Alle ansehen",
  buscar: "Suche",
  buscarTitulo: "Im Restaurantführer suchen",
  buscarPlaceholder: "Bar, Restaurant oder Gemeinde suchen",
  buscarBoton: "Suchen",
  buscarSinResultados: "Es gibt kein Lokal mit diesem Namen.",
  buscarDaloDeAlta: "Wenn es Ihr Lokal ist, können Sie es kostenlos eintragen.",
  municipios: "Gemeinden",
  negocios: "Lokale",
  porTipoDeNegocio: "Nach Art des Lokals",
  porCaracteristica: "Nach Ausstattung",
  porMunicipio: "Nach Gemeinde",
  portadaTitulo: "Führer für Bars und Restaurants",
  portadaIntro:
    "Die Gastronomiebetriebe jeder Gemeinde, mit Adresse, Öffnungszeiten und – wenn sie veröffentlicht ist – ihrer Speisekarte mit Preisen.",
  portadaCtaTitulo: "Haben Sie eine Bar oder ein Restaurant?",
  portadaCtaTexto:
    "Erstellen Sie Ihre digitale Speisekarte kostenlos und veröffentlichen Sie sie hier. Ihr Lokal erscheint im Restaurantführer Ihrer Gemeinde mit Ihren Gerichten und Preisen, und Ihre Gäste sehen die Karte, bevor sie hereinkommen.",
  ctaBoton: "Speisekarte kostenlos veröffentlichen",
  altaTitulo: "Ihr Lokal fehlt?",
  altaTexto:
    "Legen Sie es kostenlos an: wir prüfen es, veröffentlichen es im Restaurantführer und danach können Sie Ihre Karte hochladen.",
  altaBoton: "Mein Lokal eintragen",
  ctaMunicipioTitulo: "Fehlt Ihr Lokal, oder wollen Sie Ihre Karte veröffentlichen?",
  ctaMunicipioTexto:
    "Erstellen Sie Ihre digitale Speisekarte kostenlos mit Zindra: Sie erscheint auf dieser Seite, mit Ihren Gerichten, Ihren Preisen und aktuellen Öffnungszeiten.",
  ctaIslaTitulo: (isla) => `Liegt Ihr Lokal auf ${isla}?`,
  ctaIslaTexto:
    "Veröffentlichen Sie Ihre Karte kostenlos und erscheinen Sie im Restaurantführer Ihrer Gemeinde, mit Ihren Gerichten und Preisen.",
  ctaCategoriaTitulo: (categoria, isla) => `Betreiben Sie ${categoria} auf ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Bars und Restaurants in ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} Gastronomiebetriebe in ${municipios} Gemeinden. Wählen Sie Ihre Gemeinde, um die Bars, Restaurants und Cafés der Gegend mit Adresse und Speisekarte zu sehen.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} Gastronomiebetriebe in ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Wer seine Karte veröffentlicht hat, zeigt hier Gerichte und Preise." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} in ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} ${categoria} in ${lugar}, mit Adresse, Öffnungszeiten und – wenn veröffentlicht – Speisekarte mit Preisen.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} Bars, Restaurants und Cafés in ${municipio} (${isla}): Adresse, Öffnungszeiten, Telefon und Speisekarte mit Preisen.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} ${categoria} in ${lugar}: Adresse, Öffnungszeiten, Telefon und Speisekarte mit Preisen.`,
  descripcionIsla: (isla) =>
    `Führer für Bars, Restaurants und Cafés auf ${isla}, nach Gemeinden, mit Adresse, Öffnungszeiten und Speisekarte.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} in ${municipio}${direccion ? ` (${direccion})` : ""}. Öffnungszeiten, Telefon, Lage und Speisekarte.`,
  negociosCuenta: (n) => `${n} Lokale`,
  paginaXdeY: (x, y) => `Lokale (Seite ${x} von ${y})`,
  anterior: "Zurück",
  cerrar: "Schließen",
  siguiente: "Weiter",
  paginas: "Seiten",
  ruta: "Navigationspfad",
  verTodosEn: (municipio) => `Alle Bars und Restaurants in ${municipio} ansehen`,
  negocioDeHosteleria: "Gastronomiebetrieb",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} in ${municipio}, ${isla}`,
  datosDeContacto: "Kontakt",
  horario: "Öffnungszeiten",
  verEnElMapa: "Auf der Karte ansehen",
  mapaCompleto: "Größere Karte ansehen",
  comoLlegar: "Route planen",
  activarMapa: "Zum Bewegen der Karte klicken",
  otrosNegociosEn: (municipio) => `Weitere Lokale in ${municipio}`,
  sinCartaTitulo: "Dieses Lokal hat seine Speisekarte noch nicht veröffentlicht",
  sinCartaReclamada: "Die Karte erscheint hier, sobald sie veröffentlicht wird.",
  sinCartaLibre:
    "Wenn es Ihr Lokal ist, können Sie den Eintrag übernehmen und Ihre Karte kostenlos veröffentlichen: Ihre Gerichte und Preise erscheinen auf genau dieser Seite.",
  reclamarFicha: "Diesen Eintrag übernehmen",
  esTuNegocio:
    "Ist das Ihr Lokal? Übernehmen Sie den Eintrag, um die Daten zu korrigieren und Ihre Karte kostenlos zu veröffentlichen.",
  verCartaCompleta: "Vollständige Speisekarte ansehen",
  cartaEtiqueta: "Karte",
  abiertoAhora: "Jetzt geöffnet",
  abiertosAhora: "Jetzt geöffnet",
  verTodosLosNegocios: "Alle anzeigen",
  ningunoAbierto: "Gerade hat hier nichts geöffnet.",
  cerradoAhora: "Jetzt geschlossen",
  cerrado: "Geschlossen",
  abierto24h: "Durchgehend geöffnet",
  dias: ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"],
  traducidoAutomaticamente: "Automatisch übersetzt",
  servicios: "Ausstattung",
  formasDePago: "Zahlungsarten",
  precio: "Preis",
  precioMedio: "Durchschnittspreis pro Gericht",
  precioDelPlato: (min, max) => `Von ${min} bis ${max}`,
  platosEnLaCarta: (n) => `${n} Gerichte auf der Karte`,
  cartaEn: "Karte auf",
  reservarMesa: "Tischreservierung möglich",
  pedirEnMesa: "Bestellen am Tisch möglich",
  enRedes: "Social Media",
  cartaEnOtraWeb: "Speisekarte auf eigener Website",
  datoVerificado: (fecha) => `Daten geprüft am ${fecha}`,
  valoraciones: "Bewertungen",
  popularidad: "Beliebtheit",
  michelin: (n) => `${n} ${n === 1 ? "Michelin-Stern" : "Michelin-Sterne"}`,
  popularAlta: "Sehr gut besucht",
  popularMedia: "Gut besucht",
  popularBaja: "Wenig besucht",
  segunPlataforma: (n) => `${n} Bewertungen`,
  opinionesTitulo: "Wie war es?",
  opinionesSubtitulo: "Bewerten Sie Ihren Besuch",
  sinOpiniones: "Zu diesem Lokal gibt es noch keine Meinungen.",
  fotosDeGoogle: "Fotos von Google",
  recomendados: "Empfohlen",
  faq: {
    titulo: "Häufige Fragen",
    cocina: (n: string) => `Welche Küche bietet ${n}?`,
    precio: (n: string) => `Was kostet das Essen bei ${n}?`,
    terraza: (n: string) => `Hat ${n} eine Terrasse?`,
    accesible: (n: string) => `Ist ${n} barrierefrei?`,
    llevar: (n: string) => `Kann man bei ${n} zum Mitnehmen bestellen?`,
    tarjeta: (n: string) => `Kann man bei ${n} mit Karte zahlen?`,
    siTerraza: "Ja, mit Terrasse.",
    siAccesible: "Ja, der Eingang ist barrierefrei.",
    siLlevar: "Ja, Bestellungen zum Mitnehmen sind möglich.",
    siTarjeta: "Ja, Kartenzahlung wird akzeptiert.",
  },
  recomendadosCta: "Möchtest du, dass dein Lokal hier erscheint?",
  recomendadosPlan: "Zum Professional-Tarif wechseln",
  opinionesEnGoogle: "Bewertungen bei Google",
  leerEnGoogle: "Auf Google lesen",
  verTodasEnGoogle: "Alle bei Google Maps ansehen",
  resenasDeGoogleAviso: "Von ihren Verfassern bei Google veröffentlichte Bewertungen. Es sind keine Zindra-Bewertungen und sie werden nicht mit ihnen vermischt.",
  iaPros: "Vorteile",
  iaContras: "Nachteile",
  reservar: "Reservieren",
  reservarLlamando: (nombre) => `${nombre} anrufen, um zu reservieren`,
  pedirParaLlevar: "Zum Mitnehmen bestellen",
  llamarParaPedir: "Lokal anrufen",
  sinPedirParaLlevar: (municipio) => `In ${municipio} gibt es keine Bestellung zum Mitnehmen über Glovo, Uber Eats oder Just Eat`,
  iaTitulo: (nombre) => `${nombre}: Bewertungen, Vor- und Nachteile`,
  iaSegun: (n) => `KI-Zusammenfassung von ${numero("de", n)} Gästebewertungen`,
  iaAviso: "Mit künstlicher Intelligenz erstellte Zusammenfassung der bei Google und Zindra veröffentlichten Bewertungen. Sie kann Fehler enthalten: Die Bewertungen stehen weiter unten.",
  iaActualizado: (mes) => `Aktualisiert: ${mes}`,
  precioMuyBarato: "Sehr günstig",
  precioModerado: "Mittel",
  precioCaro: "Teuer",
  precioBastanteCaro: "Sehr teuer",
  precioPorPersona: (rango) => `${rango} pro Person`,
  segunGoogle: "laut Google",
  escribirOpinion: "Meinung schreiben",
  respuestaDelNegocio: "Antwort des Lokals",
  respuestaEscritaEnZindra: "Antwort des Lokals, auf Zindra verfasst (nicht auf Google)",
  opinionEnviada: "Danke für Ihre Meinung",
  opinionEnRevision: "Wir prüfen sie und veröffentlichen sie innerhalb von 24 Stunden.",
  campoValoracion: "Ihre Bewertung",
  campoTitulo: "Titel",
  campoOpinion: "Ihre Meinung",
  publicarOpinion: "Veröffentlichen",
  avisoOsmAntes: "Ein Teil der Kontakt- und Standortdaten stammt von ",
  avisoOsmDespues:
    ", unter ODbL-Lizenz. Stimmt etwas nicht, kann das Lokal es selbst korrigieren, sobald es seinen Eintrag übernimmt.",
  sobreComerEn: (municipio) => `Essen gehen in ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `Wie viele Bars und Restaurants gibt es in ${municipio}?`,
        respuesta: `Im Restaurantführer von Zindra stehen ${total.toLocaleString("de-DE")} Gastronomiebetriebe in ${municipio} (${isla}): Bars, Restaurants, Cafés und Imbisse. Jeder hat seinen Eintrag mit Adresse, Telefon und Öffnungszeiten.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `Welche Arten von Restaurants gibt es in ${municipio}?`,
        respuesta: `Am häufigsten sind in ${municipio} ${unir(categorias.map((c) => `${c.cuantos} ${c.nombre}`), "und")}. Jede Art hat ihre eigene Seite im Restaurantführer.`,
      });
    }
    preguntas.push({
      pregunta: "Kann man die Speisekarte vorher ansehen?",
      respuesta:
        conCarta > 0
          ? `Ja. ${conCarta === 1 ? "Ein Lokal" : `${conCarta} Lokale`} in ${municipio} ${conCarta === 1 ? "hat" : "haben"} die Karte mit Gerichten und Preisen veröffentlicht, direkt auf dem Handy und ohne App.`
          : `In ${municipio} hat noch kein Lokal seine Karte veröffentlicht. Wer sie veröffentlicht, zeigt Gerichte und Preise in seinem Eintrag.`,
    });
    preguntas.push({
      pregunta: `Ich habe eine Bar in ${municipio}. Wie komme ich hier hinein?`,
      respuesta: `Legen Sie Ihre digitale Speisekarte bei Zindra an – das ist kostenlos – und geben Sie sie für den Restaurantführer frei. Steht Ihr Lokal schon in der Liste, können Sie den Eintrag direkt dort übernehmen. Je vollständiger er ist, desto weiter oben erscheint er auf dieser Seite.`,
    });
    return preguntas;
  },
};

const en: Textos = {
  directorio: "Restaurant guide",
  guiaDe: (isla) => `${isla} restaurant guide`,
  guiaIntro: (total, municipios) =>
    `${total} bars and restaurants in ${municipios} towns, with opening hours, the menu where there is one and what people who have been there think.`,
  dondeComerEn: (lugar) => `Where to eat in ${lugar}`,
  todosLosMunicipios: "All towns",
  queTeApetece: "What are you in the mood for?",
  queTeApeteceEn: (lugar) => `What are you in the mood for in ${lugar}?`,
  todosLosTipos: "Every cuisine and type of place",
  masOpciones: "More types and features",
  elegirMunicipio: "Choose a town",
  masPopularesEn: (lugar) => `Most popular in ${lugar}`,
  masPopularesNota: "Ranked by how many reviews they have.",
  planes: "By occasion",
  verTodos: "See all",
  buscar: "Search",
  buscarTitulo: "Search the guide",
  buscarPlaceholder: "Search for a bar, a restaurant or a town",
  buscarBoton: "Search",
  buscarSinResultados: "No business goes by that name.",
  buscarDaloDeAlta: "If it is yours, you can list it for free.",
  municipios: "Towns",
  negocios: "Places",
  porTipoDeNegocio: "By type of place",
  porCaracteristica: "By what it offers",
  porMunicipio: "By town",
  portadaTitulo: "Guide to bars and restaurants",
  portadaIntro:
    "The places to eat and drink in every town, with their address, their opening hours and, when they have published it, their menu with prices.",
  portadaCtaTitulo: "Do you run a bar or a restaurant?",
  portadaCtaTexto:
    "Create your digital menu for free and publish it here. Your business appears in your town's guide with your dishes and your prices, and your customers can read it before walking in.",
  ctaBoton: "Publish my menu for free",
  altaTitulo: "Your business not here?",
  altaTexto:
    "Add it for free: we check it, publish it in the guide, and from there you can upload your menu.",
  altaBoton: "Add my business",
  ctaMunicipioTitulo: "Is your place missing, or do you want to publish your menu?",
  ctaMunicipioTexto:
    "Create your digital menu with Zindra for free and it will show up on this page, with your dishes, your prices and your opening hours kept up to date.",
  ctaIslaTitulo: (isla) => `Is your business in ${isla}?`,
  ctaIslaTexto:
    "Publish your menu for free and appear in your town's guide with your dishes and your prices.",
  ctaCategoriaTitulo: (categoria, isla) => `Do you run ${categoria} in ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Bars and restaurants in ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} places to eat and drink across ${municipios} towns. Pick yours to see the bars, restaurants and cafés in the area with their address and their menu.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} places to eat and drink in ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " The ones that have published their menu show their dishes and prices." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} in ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} ${categoria} in ${lugar}, with address, opening hours and, when published, their menu with prices.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} bars, restaurants and cafés in ${municipio} (${isla}): address, opening hours, phone and menu with prices.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} ${categoria} in ${lugar}: address, opening hours, phone and menu with prices.`,
  descripcionIsla: (isla) =>
    `Guide to bars, restaurants and cafés in ${isla}, town by town, with address, opening hours and menu.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} in ${municipio}${direccion ? ` (${direccion})` : ""}. Opening hours, phone, location and menu.`,
  negociosCuenta: (n) => `${n} places`,
  paginaXdeY: (x, y) => `Places (page ${x} of ${y})`,
  anterior: "Previous",
  cerrar: "Close",
  siguiente: "Next",
  paginas: "Pages",
  ruta: "Breadcrumb",
  verTodosEn: (municipio) => `See every bar and restaurant in ${municipio}`,
  negocioDeHosteleria: "Place to eat and drink",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} in ${municipio}, ${isla}`,
  datosDeContacto: "Contact details",
  horario: "Opening hours",
  verEnElMapa: "See on the map",
  mapaCompleto: "See the full map",
  comoLlegar: "Get directions",
  activarMapa: "Click to move the map",
  otrosNegociosEn: (municipio) => `Other places in ${municipio}`,
  sinCartaTitulo: "This place has not published its menu yet",
  sinCartaReclamada: "Its menu will show up here as soon as it is published.",
  sinCartaLibre:
    "If this is your business, you can claim the listing and publish your menu for free: your dishes and your prices will appear on this very page.",
  reclamarFicha: "Claim this listing",
  esTuNegocio:
    "Is this your business? Claim the listing to fix the details and publish your menu for free.",
  verCartaCompleta: "See the full menu",
  cartaEtiqueta: "Menu",
  abiertoAhora: "Open now",
  abiertosAhora: "Open now",
  verTodosLosNegocios: "See all",
  ningunoAbierto: "Nothing is open around here right now.",
  cerradoAhora: "Closed now",
  cerrado: "Closed",
  abierto24h: "Open 24 h",
  dias: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  traducidoAutomaticamente: "Machine translated",
  servicios: "Facilities",
  formasDePago: "Payment methods",
  precio: "Price",
  precioMedio: "Average price per dish",
  precioDelPlato: (min, max) => `From ${min} to ${max}`,
  platosEnLaCarta: (n) => `${n} dishes on the menu`,
  cartaEn: "Menu in",
  reservarMesa: "Table booking available",
  pedirEnMesa: "You can order from the table",
  enRedes: "Social media",
  cartaEnOtraWeb: "Their menu on another site",
  datoVerificado: (fecha) => `Details checked on ${fecha}`,
  valoraciones: "Ratings",
  popularidad: "Popularity",
  michelin: (n) => `${n} Michelin ${n === 1 ? "star" : "stars"}`,
  popularAlta: "Very busy",
  popularMedia: "Quite busy",
  popularBaja: "Not very busy",
  segunPlataforma: (n) => `${n} ratings`,
  opinionesTitulo: "How was it?",
  opinionesSubtitulo: "Rate your experience",
  sinOpiniones: "There are no reviews of this place yet.",
  fotosDeGoogle: "Photos from Google",
  recomendados: "Recommended",
  faq: {
    titulo: "Frequently asked questions",
    cocina: (n: string) => `What kind of food does ${n} serve?`,
    precio: (n: string) => `How much does it cost to eat at ${n}?`,
    terraza: (n: string) => `Does ${n} have a terrace?`,
    accesible: (n: string) => `Is ${n} wheelchair accessible?`,
    llevar: (n: string) => `Can you order takeaway at ${n}?`,
    tarjeta: (n: string) => `Does ${n} take cards?`,
    siTerraza: "Yes, it has a terrace.",
    siAccesible: "Yes, the entrance is accessible.",
    siLlevar: "Yes, takeaway is available.",
    siTarjeta: "Yes, cards are accepted.",
  },
  recomendadosCta: "Want your business featured here?",
  recomendadosPlan: "Get the Professional plan",
  opinionesEnGoogle: "Reviews on Google",
  leerEnGoogle: "Read on Google",
  verTodasEnGoogle: "See them all on Google Maps",
  resenasDeGoogleAviso: "Reviews published on Google by their authors. They are not Zindra reviews and are not mixed with them.",
  iaPros: "Pros",
  iaContras: "Cons",
  reservar: "Book a table",
  reservarLlamando: (nombre) => `Call ${nombre} to book a table`,
  pedirParaLlevar: "Order takeaway",
  llamarParaPedir: "Call the restaurant",
  sinPedirParaLlevar: (municipio) => `No takeaway ordering via Glovo, Uber Eats or Just Eat in ${municipio}`,
  iaTitulo: (nombre) => `${nombre} reviews: pros and cons`,
  iaSegun: (n) => `AI summary of ${numero("en", n)} customer reviews`,
  iaAviso: "Summary written with artificial intelligence from the reviews published on Google and Zindra. It may contain mistakes: the reviews are further down.",
  iaActualizado: (mes) => `Updated ${mes}`,
  precioMuyBarato: "Very cheap",
  precioModerado: "Moderate",
  precioCaro: "Expensive",
  precioBastanteCaro: "Very expensive",
  precioPorPersona: (rango) => `${rango} per person`,
  segunGoogle: "according to Google",
  escribirOpinion: "Write a review",
  respuestaDelNegocio: "Reply from the business",
  respuestaEscritaEnZindra: "Reply from the business, written on Zindra (not on Google)",
  opinionEnviada: "Thank you for your review",
  opinionEnRevision: "We check it and publish it within 24 hours.",
  campoValoracion: "Your rating",
  campoTitulo: "Title",
  campoOpinion: "Your review",
  publicarOpinion: "Publish",
  avisoOsmAntes: "Some of the contact and location data comes from ",
  avisoOsmDespues:
    ", under the ODbL licence. If something is wrong, the business itself can fix it when it claims its listing.",
  sobreComerEn: (municipio) => `About eating out in ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `How many bars and restaurants are there in ${municipio}?`,
        respuesta: `The Zindra guide lists ${total.toLocaleString("en-GB")} places to eat and drink in ${municipio} (${isla}): bars, restaurants, cafés and takeaways. Each one has its listing with address, phone and opening hours.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `What kinds of restaurant are there in ${municipio}?`,
        respuesta: `The most common in ${municipio} are ${unir(categorias.map((c) => `${c.cuantos} ${c.nombre}`), "and")}. Each kind has its own page inside the guide.`,
      });
    }
    preguntas.push({
      pregunta: "Can I see the menu before going?",
      respuesta:
        conCarta > 0
          ? `Yes. ${conCarta === 1 ? "One place" : `${conCarta} places`} in ${municipio} ${conCarta === 1 ? "has" : "have"} published their menu, with dishes and prices, readable on your phone with nothing to install.`
          : `No place in ${municipio} has published its menu yet. Those that do will show their dishes and prices on their listing.`,
    });
    preguntas.push({
      pregunta: `I run a bar in ${municipio}. How do I get listed?`,
      respuesta: `Create your digital menu with Zindra — it is free — and tick the box that publishes it to the guide. If your business already appears in the list, you can claim its listing right there. The more complete it is, the higher it shows on this page.`,
    });
    return preguntas;
  },
};

const ca: Textos = {
  directorio: "Guia de restaurants",
  guiaDe: (isla) => `Guia de restaurants de ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} bars i restaurants a ${municipios} municipis, amb el seu horari, la carta quan en tenen i el que en pensa qui ja hi ha anat.`,
  dondeComerEn: (lugar) => `On menjar a ${lugar}`,
  todosLosMunicipios: "Tots els municipis",
  queTeApetece: "Què et ve de gust?",
  queTeApeteceEn: (lugar) => `Què et ve de gust a ${lugar}?`,
  todosLosTipos: "Tots els tipus de cuina i de local",
  masOpciones: "Més tipus i serveis",
  elegirMunicipio: "Tria municipi",
  masPopularesEn: (lugar) => `Els més populars de ${lugar}`,
  masPopularesNota: "Ordenats pel nombre d'opinions que acumulen.",
  planes: "Segons el pla",
  verTodos: "Veure-ho tot",
  buscar: "Cercar",
  buscarTitulo: "Cercar a la guia",
  buscarPlaceholder: "Cerca un bar, un restaurant o un municipi",
  buscarBoton: "Cercar",
  buscarSinResultados: "No hi ha cap negoci que es digui així.",
  buscarDaloDeAlta: "Si és el teu, el pots donar d'alta gratis.",
  municipios: "Municipis",
  negocios: "Negocis",
  porTipoDeNegocio: "Per tipus de negoci",
  porCaracteristica: "Pel que ofereix",
  porMunicipio: "Per municipi",
  portadaTitulo: "Guia de bars i restaurants",
  portadaIntro:
    "Els negocis d'hostaleria de cada localitat, amb l'adreça, l'horari i, quan la tenen publicada, la carta amb els preus.",
  portadaCtaTitulo: "Tens un bar o un restaurant?",
  portadaCtaTexto:
    "Crea la teva carta digital gratis i publica-la aquí. El teu negoci apareix a la guia de la teva localitat amb els teus plats i els teus preus, i els clients la poden veure abans d'entrar.",
  ctaBoton: "Publicar la meva carta gratis",
  altaTitulo: "El teu negoci no hi és?",
  altaTexto:
    "Crea'l gratis: el revisem, el publiquem a la guia i des d'allà pots pujar la teva carta.",
  altaBoton: "Donar d'alta el meu negoci",
  ctaMunicipioTitulo: "Hi falta el teu negoci o vols publicar la teva carta?",
  ctaMunicipioTexto:
    "Crea la teva carta digital gratis amb Zindra i sortirà en aquesta pàgina, amb els teus plats, els teus preus i l'horari al dia.",
  ctaIslaTitulo: (isla) => `El teu negoci és a ${isla}?`,
  ctaIslaTexto:
    "Publica la teva carta gratis i apareix a la guia del teu municipi amb els teus plats i els teus preus.",
  ctaCategoriaTitulo: (categoria, isla) => `Tens ${categoria} a ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Bars i restaurants a ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} negocis d'hostaleria repartits en ${municipios} municipis. Tria el teu per veure els bars, restaurants i cafeteries de la zona amb l'adreça i la carta.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} negocis d'hostaleria a ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Els que tenen la carta publicada hi mostren els plats i els preus." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} a ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} ${categoria} a ${lugar}, amb adreça, horari i, quan la tenen publicada, la carta amb els preus.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} bars, restaurants i cafeteries a ${municipio} (${isla}): adreça, horari, telèfon i carta amb preus.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} ${categoria} a ${lugar}: adreça, horari, telèfon i carta amb preus.`,
  descripcionIsla: (isla) =>
    `Guia de bars, restaurants i cafeteries de ${isla} per municipi, amb adreça, horari i carta.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} a ${municipio}${direccion ? ` (${direccion})` : ""}. Horari, telèfon, ubicació i carta.`,
  negociosCuenta: (n) => `${n} negocis`,
  paginaXdeY: (x, y) => `Negocis (pàgina ${x} de ${y})`,
  anterior: "Anterior",
  cerrar: "Tancar",
  siguiente: "Següent",
  paginas: "Pàgines",
  ruta: "Ruta",
  verTodosEn: (municipio) => `Veure tots els bars i restaurants de ${municipio}`,
  negocioDeHosteleria: "Negoci d'hostaleria",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} a ${municipio}, ${isla}`,
  datosDeContacto: "Dades de contacte",
  horario: "Horari",
  verEnElMapa: "Veure al mapa",
  mapaCompleto: "Veure el mapa complet",
  comoLlegar: "Com arribar-hi",
  activarMapa: "Prem per moure el mapa",
  otrosNegociosEn: (municipio) => `Altres negocis a ${municipio}`,
  sinCartaTitulo: "Aquest negoci encara no ha publicat la carta",
  sinCartaReclamada: "La carta hi sortirà tan bon punt la publiqui.",
  sinCartaLibre:
    "Si és el teu negoci, pots reclamar la fitxa i publicar la carta gratis: els teus plats i els teus preus sortiran en aquesta mateixa pàgina.",
  reclamarFicha: "Reclamar aquesta fitxa",
  esTuNegocio:
    "És el teu negoci? Reclama la fitxa per corregir les dades i publicar la carta gratis.",
  verCartaCompleta: "Veure la carta completa",
  cartaEtiqueta: "Carta",
  abiertoAhora: "Obert ara",
  abiertosAhora: "Oberts ara",
  verTodosLosNegocios: "Veure tots",
  ningunoAbierto: "Ara mateix no n'hi ha cap d'obert per aquí.",
  cerradoAhora: "Tancat ara",
  cerrado: "Tancat",
  abierto24h: "Obert 24 h",
  dias: ["Dilluns", "Dimarts", "Dimecres", "Dijous", "Divendres", "Dissabte", "Diumenge"],
  traducidoAutomaticamente: "Traduït automàticament",
  servicios: "Serveis",
  formasDePago: "Formes de pagament",
  precio: "Preu",
  precioMedio: "Preu mitjà del plat",
  precioDelPlato: (min, max) => `De ${min} a ${max}`,
  platosEnLaCarta: (n) => `${n} plats a la carta`,
  cartaEn: "Carta en",
  reservarMesa: "Es pot reservar taula",
  pedirEnMesa: "Es pot demanar des de la taula",
  enRedes: "A les xarxes",
  cartaEnOtraWeb: "La seva carta en una altra web",
  datoVerificado: (fecha) => `Dades verificades el ${fecha}`,
  valoraciones: "Valoracions",
  popularidad: "Popularitat",
  michelin: (n) => `${n} ${n === 1 ? "estrella" : "estrelles"} Michelin`,
  popularAlta: "Molt visitat",
  popularMedia: "Força visitat",
  popularBaja: "Poc visitat",
  segunPlataforma: (n) => `${n} valoracions`,
  opinionesTitulo: "Què t'ha semblat?",
  opinionesSubtitulo: "Valora la teva experiència",
  sinOpiniones: "Encara no hi ha opinions d'aquest lloc.",
  fotosDeGoogle: "Fotos de Google",
  recomendados: "Recomanats",
  faq: {
    titulo: "Preguntes freqüents",
    cocina: (n: string) => `Quin tipus de cuina fa ${n}?`,
    precio: (n: string) => `Quant costa menjar a ${n}?`,
    terraza: (n: string) => `${n} té terrassa?`,
    accesible: (n: string) => `${n} és accessible per a cadires de rodes?`,
    llevar: (n: string) => `Es pot demanar per emportar a ${n}?`,
    tarjeta: (n: string) => `Es pot pagar amb targeta a ${n}?`,
    siTerraza: "Sí, té terrassa.",
    siAccesible: "Sí, l'entrada és accessible.",
    siLlevar: "Sí, es pot demanar per emportar.",
    siTarjeta: "Sí, accepten targeta.",
  },
  recomendadosCta: "Vols que el teu negoci aparegui a Recomanats?",
  recomendadosPlan: "Subscriu-te al pla Professional",
  opinionesEnGoogle: "Opinions a Google",
  leerEnGoogle: "Llegir a Google",
  verTodasEnGoogle: "Veure-les totes a Google Maps",
  resenasDeGoogleAviso: "Opinions publicades a Google pels seus autors. No són opinions de Zindra ni s'hi barregen.",
  iaPros: "Pros",
  iaContras: "Contres",
  reservar: "Reservar",
  reservarLlamando: (nombre) => `Trucar a ${nombre} per reservar`,
  pedirParaLlevar: "Demanar per endur",
  llamarParaPedir: "Trucar al local",
  sinPedirParaLlevar: (municipio) => `A ${municipio} no hi ha comandes per endur per Glovo, Uber Eats ni Just Eat`,
  iaTitulo: (nombre) => `Opinions de ${nombre}: pros i contres`,
  iaSegun: (n) => `Resum amb IA de ${numero("ca", n)} opinions de clients`,
  iaAviso: "Resum fet amb intel·ligència artificial a partir de les opinions publicades a Google i a Zindra. Pot contenir errors: les opinions són més avall.",
  iaActualizado: (mes) => `Actualitzat: ${mes}`,
  precioMuyBarato: "Molt barat",
  precioModerado: "Moderat",
  precioCaro: "Car",
  precioBastanteCaro: "Bastant car",
  precioPorPersona: (rango) => `${rango} per persona`,
  segunGoogle: "segons Google",
  escribirOpinion: "Escriure una opinió",
  respuestaDelNegocio: "Resposta del negoci",
  respuestaEscritaEnZindra: "Resposta del negoci, escrita a Zindra (no a Google)",
  opinionEnviada: "Gràcies per la teva opinió",
  opinionEnRevision: "La revisem i es publica en menys de 24 hores.",
  campoValoracion: "La teva valoració",
  campoTitulo: "Títol",
  campoOpinion: "La teva opinió",
  publicarOpinion: "Publicar",
  avisoOsmAntes: "Part de les dades de contacte i ubicació provenen d'",
  avisoOsmDespues:
    ", amb llicència ODbL. Si hi ha res que no estigui bé, el mateix negoci ho pot corregir quan reclami la fitxa.",
  sobreComerEn: (municipio) => `Sobre menjar a ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `Quants bars i restaurants hi ha a ${municipio}?`,
        respuesta: `A la guia de Zindra hi ha ${total.toLocaleString("ca-ES")} negocis d'hostaleria a ${municipio} (${isla}), entre bars, restaurants, cafeteries i menjar per emportar. Cadascun té la seva fitxa amb adreça, telèfon i horari.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `Quins tipus de restaurant hi ha a ${municipio}?`,
        respuesta: `Els més nombrosos a ${municipio} són ${unir(categorias.map((c) => `${c.cuantos} ${c.nombre}`), "i")}. Cada tipus té la seva pàgina dins de la guia.`,
      });
    }
    preguntas.push({
      pregunta: "Es pot veure la carta abans d'anar-hi?",
      respuesta:
        conCarta > 0
          ? `Sí. ${conCarta === 1 ? "Un negoci" : `${conCarta} negocis`} de ${municipio} ${conCarta === 1 ? "té" : "tenen"} la carta publicada, amb els plats i els preus, i es veu des del mòbil sense instal·lar res.`
          : `Encara no hi ha cap negoci de ${municipio} amb la carta publicada. Els que la publiquin mostraran els plats i els preus a la seva fitxa.`,
    });
    preguntas.push({
      pregunta: `Tinc un bar a ${municipio}, com hi surto?`,
      respuesta: `Creant la teva carta digital a Zindra, que és gratis, i marcant-la perquè es vegi a la guia. Si el teu negoci ja surt a la llista, en pots reclamar la fitxa des d'allà. Com més completa estigui, més amunt surt en aquesta pàgina.`,
    });
    return preguntas;
  },
};

const fr: Textos = {
  directorio: "Guide des restaurants",
  guiaDe: (isla) => `Guide des restaurants de ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} bars et restaurants dans ${municipios} communes, avec leurs horaires, leur carte quand ils en ont une et l'avis de ceux qui y sont allés.`,
  dondeComerEn: (lugar) => `Où manger à ${lugar}`,
  todosLosMunicipios: "Toutes les communes",
  queTeApetece: "Qu'est-ce qui vous tente ?",
  queTeApeteceEn: (lugar) => `Qu'est-ce qui vous tente à ${lugar} ?`,
  todosLosTipos: "Toutes les cuisines et tous les types d'établissement",
  masOpciones: "Plus de types et de services",
  elegirMunicipio: "Choisir une commune",
  masPopularesEn: (lugar) => `Les plus populaires à ${lugar}`,
  masPopularesNota: "Classés selon le nombre d'avis reçus.",
  planes: "Selon l'envie",
  verTodos: "Tout voir",
  buscar: "Recherche",
  buscarTitulo: "Rechercher dans le guide",
  buscarPlaceholder: "Cherchez un bar, un restaurant ou une commune",
  buscarBoton: "Rechercher",
  buscarSinResultados: "Aucun établissement ne porte ce nom.",
  buscarDaloDeAlta: "Si c'est le vôtre, vous pouvez l'inscrire gratuitement.",
  municipios: "Communes",
  negocios: "Établissements",
  porTipoDeNegocio: "Par type d'établissement",
  porCaracteristica: "Par prestation",
  porMunicipio: "Par commune",
  portadaTitulo: "Guide des bars et restaurants",
  portadaIntro:
    "Les établissements de restauration de chaque commune, avec leur adresse, leurs horaires et, lorsqu'elle est publiée, leur carte avec les prix.",
  portadaCtaTitulo: "Vous avez un bar ou un restaurant ?",
  portadaCtaTexto:
    "Créez votre carte numérique gratuitement et publiez-la ici. Votre établissement apparaît dans le guide de votre commune avec vos plats et vos prix, et vos clients la consultent avant d'entrer.",
  ctaBoton: "Publier ma carte gratuitement",
  altaTitulo: "Votre établissement n'y est pas ?",
  altaTexto:
    "Inscrivez-le gratuitement : nous le vérifions, nous le publions dans le guide et vous pouvez ensuite mettre votre carte en ligne.",
  altaBoton: "Inscrire mon établissement",
  ctaMunicipioTitulo: "Votre établissement manque, ou vous voulez publier votre carte ?",
  ctaMunicipioTexto:
    "Créez votre carte numérique gratuitement avec Zindra : elle apparaîtra sur cette page, avec vos plats, vos prix et vos horaires à jour.",
  ctaIslaTitulo: (isla) => `Votre établissement est à ${isla} ?`,
  ctaIslaTexto:
    "Publiez votre carte gratuitement et apparaissez dans le guide de votre commune, avec vos plats et vos prix.",
  ctaCategoriaTitulo: (categoria, isla) => `Vous tenez ${categoria} à ${isla} ?`,
  baresYRestaurantesEn: (lugar) => `Bars et restaurants à ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} établissements de restauration répartis dans ${municipios} communes. Choisissez la vôtre pour voir les bars, restaurants et cafés du coin avec leur adresse et leur carte.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} établissements de restauration à ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Ceux qui ont publié leur carte y affichent leurs plats et leurs prix." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} à ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} ${categoria} à ${lugar}, avec adresse, horaires et, lorsqu'elle est publiée, la carte avec les prix.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} bars, restaurants et cafés à ${municipio} (${isla}) : adresse, horaires, téléphone et carte avec les prix.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} ${categoria} à ${lugar} : adresse, horaires, téléphone et carte avec les prix.`,
  descripcionIsla: (isla) =>
    `Guide des bars, restaurants et cafés de ${isla}, commune par commune, avec adresse, horaires et carte.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} à ${municipio}${direccion ? ` (${direccion})` : ""}. Horaires, téléphone, emplacement et carte.`,
  negociosCuenta: (n) => `${n} établissements`,
  paginaXdeY: (x, y) => `Établissements (page ${x} sur ${y})`,
  anterior: "Précédent",
  cerrar: "Fermer",
  siguiente: "Suivant",
  paginas: "Pages",
  ruta: "Fil d'Ariane",
  verTodosEn: (municipio) => `Voir tous les bars et restaurants de ${municipio}`,
  negocioDeHosteleria: "Établissement de restauration",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} à ${municipio}, ${isla}`,
  datosDeContacto: "Coordonnées",
  horario: "Horaires",
  verEnElMapa: "Voir sur la carte",
  mapaCompleto: "Voir la carte en grand",
  comoLlegar: "Itinéraire",
  activarMapa: "Cliquez pour déplacer la carte",
  otrosNegociosEn: (municipio) => `Autres établissements à ${municipio}`,
  sinCartaTitulo: "Cet établissement n'a pas encore publié sa carte",
  sinCartaReclamada: "Sa carte apparaîtra ici dès qu'elle sera publiée.",
  sinCartaLibre:
    "Si c'est votre établissement, vous pouvez revendiquer la fiche et publier votre carte gratuitement : vos plats et vos prix apparaîtront sur cette même page.",
  reclamarFicha: "Revendiquer cette fiche",
  esTuNegocio:
    "C'est votre établissement ? Revendiquez la fiche pour corriger les informations et publier votre carte gratuitement.",
  verCartaCompleta: "Voir la carte complète",
  cartaEtiqueta: "Carte",
  abiertoAhora: "Ouvert maintenant",
  abiertosAhora: "Ouverts maintenant",
  verTodosLosNegocios: "Tout voir",
  ningunoAbierto: "Rien n'est ouvert par ici en ce moment.",
  cerradoAhora: "Fermé maintenant",
  cerrado: "Fermé",
  abierto24h: "Ouvert 24 h/24",
  dias: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"],
  traducidoAutomaticamente: "Traduit automatiquement",
  servicios: "Services",
  formasDePago: "Moyens de paiement",
  precio: "Prix",
  precioMedio: "Prix moyen par plat",
  precioDelPlato: (min, max) => `De ${min} à ${max}`,
  platosEnLaCarta: (n) => `${n} plats à la carte`,
  cartaEn: "Carte en",
  reservarMesa: "Réservation de table possible",
  pedirEnMesa: "Commande possible depuis la table",
  enRedes: "Réseaux sociaux",
  cartaEnOtraWeb: "Sa carte sur un autre site",
  datoVerificado: (fecha) => `Informations vérifiées le ${fecha}`,
  valoraciones: "Notes",
  popularidad: "Popularité",
  michelin: (n) => `${n} ${n === 1 ? "étoile" : "étoiles"} Michelin`,
  popularAlta: "Très fréquenté",
  popularMedia: "Assez fréquenté",
  popularBaja: "Peu fréquenté",
  segunPlataforma: (n) => `${n} avis`,
  opinionesTitulo: "Qu'en avez-vous pensé ?",
  opinionesSubtitulo: "Notez votre expérience",
  sinOpiniones: "Il n'y a pas encore d'avis sur cet établissement.",
  fotosDeGoogle: "Photos de Google",
  recomendados: "Recommandés",
  faq: {
    titulo: "Questions fréquentes",
    cocina: (n: string) => `Quel type de cuisine propose ${n} ?`,
    precio: (n: string) => `Combien coûte un repas chez ${n} ?`,
    terraza: (n: string) => `${n} a-t-il une terrasse ?`,
    accesible: (n: string) => `${n} est-il accessible en fauteuil roulant ?`,
    llevar: (n: string) => `Peut-on commander à emporter chez ${n} ?`,
    tarjeta: (n: string) => `Peut-on payer par carte chez ${n} ?`,
    siTerraza: "Oui, avec terrasse.",
    siAccesible: "Oui, l'entrée est accessible.",
    siLlevar: "Oui, la vente à emporter est possible.",
    siTarjeta: "Oui, la carte est acceptée.",
  },
  recomendadosCta: "Vous voulez voir votre établissement ici ?",
  recomendadosPlan: "Passer au plan Professional",
  opinionesEnGoogle: "Avis sur Google",
  leerEnGoogle: "Lire sur Google",
  verTodasEnGoogle: "Voir tous les avis sur Google Maps",
  resenasDeGoogleAviso: "Avis publiés sur Google par leurs auteurs. Ce ne sont pas des avis Zindra et ils ne sont pas mélangés avec eux.",
  iaPros: "Points forts",
  iaContras: "Points faibles",
  reservar: "Réserver",
  reservarLlamando: (nombre) => `Appeler ${nombre} pour réserver`,
  pedirParaLlevar: "Commander à emporter",
  llamarParaPedir: "Appeler le restaurant",
  sinPedirParaLlevar: (municipio) => `Pas de commande à emporter via Glovo, Uber Eats ou Just Eat à ${municipio}`,
  iaTitulo: (nombre) => `Avis sur ${nombre} : points forts et points faibles`,
  iaSegun: (n) => `Résumé par IA de ${numero("fr", n)} avis de clients`,
  iaAviso: "Résumé rédigé par intelligence artificielle à partir des avis publiés sur Google et Zindra. Il peut contenir des erreurs : les avis se trouvent plus bas.",
  iaActualizado: (mes) => `Mis à jour : ${mes}`,
  precioMuyBarato: "Très bon marché",
  precioModerado: "Modéré",
  precioCaro: "Cher",
  precioBastanteCaro: "Assez cher",
  precioPorPersona: (rango) => `${rango} par personne`,
  segunGoogle: "selon Google",
  escribirOpinion: "Écrire un avis",
  respuestaDelNegocio: "Réponse de l'établissement",
  respuestaEscritaEnZindra: "Réponse de l'établissement, rédigée sur Zindra (pas sur Google)",
  opinionEnviada: "Merci pour votre avis",
  opinionEnRevision: "Nous le vérifions et il est publié sous 24 heures.",
  campoValoracion: "Votre note",
  campoTitulo: "Titre",
  campoOpinion: "Votre avis",
  publicarOpinion: "Publier",
  avisoOsmAntes: "Une partie des coordonnées et de la localisation provient d'",
  avisoOsmDespues:
    ", sous licence ODbL. Si quelque chose est inexact, l'établissement peut le corriger en revendiquant sa fiche.",
  sobreComerEn: (municipio) => `Manger à ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `Combien y a-t-il de bars et de restaurants à ${municipio} ?`,
        respuesta: `Le guide Zindra recense ${total.toLocaleString("fr-FR")} établissements de restauration à ${municipio} (${isla}) : bars, restaurants, cafés et vente à emporter. Chacun a sa fiche avec adresse, téléphone et horaires.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `Quels types de restaurants trouve-t-on à ${municipio} ?`,
        respuesta: `Les plus nombreux à ${municipio} sont ${unir(categorias.map((c) => `${c.cuantos} ${c.nombre}`), "et")}. Chaque type a sa propre page dans le guide.`,
      });
    }
    preguntas.push({
      pregunta: "Peut-on consulter la carte avant d'y aller ?",
      respuesta:
        conCarta > 0
          ? `Oui. ${conCarta === 1 ? "Un établissement" : `${conCarta} établissements`} de ${municipio} ${conCarta === 1 ? "a publié sa carte" : "ont publié leur carte"}, avec les plats et les prix, consultable depuis le téléphone sans rien installer.`
          : `Aucun établissement de ${municipio} n'a encore publié sa carte. Ceux qui le feront afficheront leurs plats et leurs prix sur leur fiche.`,
    });
    preguntas.push({
      pregunta: `J'ai un bar à ${municipio}, comment y figurer ?`,
      respuesta: `En créant votre carte numérique sur Zindra, qui est gratuite, et en la publiant dans le guide. Si votre établissement figure déjà dans la liste, vous pouvez en revendiquer la fiche directement. Plus elle est complète, plus elle remonte sur cette page.`,
    });
    return preguntas;
  },
};

const it: Textos = {
  directorio: "Guida ai ristoranti",
  guiaDe: (isla) => `Guida ai ristoranti di ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} bar e ristoranti in ${municipios} comuni, con orari, menu quando ce l'hanno e il parere di chi ci è già stato.`,
  dondeComerEn: (lugar) => `Dove mangiare a ${lugar}`,
  todosLosMunicipios: "Tutti i comuni",
  queTeApetece: "Cosa ti va?",
  queTeApeteceEn: (lugar) => `Cosa ti va a ${lugar}?`,
  todosLosTipos: "Tutte le cucine e i tipi di locale",
  masOpciones: "Altri tipi e servizi",
  elegirMunicipio: "Scegli un comune",
  masPopularesEn: (lugar) => `I più popolari a ${lugar}`,
  masPopularesNota: "Ordinati per numero di recensioni.",
  planes: "A seconda del programma",
  verTodos: "Vedi tutti",
  buscar: "Cerca",
  buscarTitulo: "Cerca nella guida",
  buscarPlaceholder: "Cerca un bar, un ristorante o un comune",
  buscarBoton: "Cerca",
  buscarSinResultados: "Non c'è nessun locale con questo nome.",
  buscarDaloDeAlta: "Se è il tuo, puoi registrarlo gratis.",
  municipios: "Comuni",
  negocios: "Locali",
  porTipoDeNegocio: "Per tipo di locale",
  porCaracteristica: "Per servizi",
  porMunicipio: "Per comune",
  portadaTitulo: "Guida a bar e ristoranti",
  portadaIntro:
    "I locali di ogni comune, con indirizzo, orari e, quando l'hanno pubblicato, il menu con i prezzi.",
  portadaCtaTitulo: "Hai un bar o un ristorante?",
  portadaCtaTexto:
    "Crea il tuo menu digitale gratis e pubblicalo qui. Il tuo locale compare nella guida del tuo comune con i tuoi piatti e i tuoi prezzi, e i clienti lo leggono prima di entrare.",
  ctaBoton: "Pubblica il mio menu gratis",
  altaTitulo: "Il tuo locale non c'è?",
  altaTexto:
    "Registralo gratis: lo controlliamo, lo pubblichiamo nella guida e da lì puoi caricare il tuo menù.",
  altaBoton: "Registra il mio locale",
  ctaMunicipioTitulo: "Manca il tuo locale, o vuoi pubblicare il tuo menu?",
  ctaMunicipioTexto:
    "Crea il tuo menu digitale gratis con Zindra e comparirà in questa pagina, con i tuoi piatti, i tuoi prezzi e gli orari aggiornati.",
  ctaIslaTitulo: (isla) => `Il tuo locale è a ${isla}?`,
  ctaIslaTexto:
    "Pubblica il tuo menu gratis e compari nella guida del tuo comune con i tuoi piatti e i tuoi prezzi.",
  ctaCategoriaTitulo: (categoria, isla) => `Hai ${categoria} a ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Bar e ristoranti a ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} locali distribuiti in ${municipios} comuni. Scegli il tuo per vedere bar, ristoranti e caffetterie della zona con indirizzo e menu.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} locali a ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Quelli che hanno pubblicato il menu ne mostrano piatti e prezzi." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} a ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} ${categoria} a ${lugar}, con indirizzo, orari e, quando è pubblicato, il menu con i prezzi.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} bar, ristoranti e caffetterie a ${municipio} (${isla}): indirizzo, orari, telefono e menu con i prezzi.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} ${categoria} a ${lugar}: indirizzo, orari, telefono e menu con i prezzi.`,
  descripcionIsla: (isla) =>
    `Guida a bar, ristoranti e caffetterie di ${isla}, comune per comune, con indirizzo, orari e menu.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} a ${municipio}${direccion ? ` (${direccion})` : ""}. Orari, telefono, posizione e menu.`,
  negociosCuenta: (n) => `${n} locali`,
  paginaXdeY: (x, y) => `Locali (pagina ${x} di ${y})`,
  anterior: "Precedente",
  cerrar: "Chiudi",
  siguiente: "Successiva",
  paginas: "Pagine",
  ruta: "Percorso",
  verTodosEn: (municipio) => `Vedi tutti i bar e i ristoranti di ${municipio}`,
  negocioDeHosteleria: "Locale",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} a ${municipio}, ${isla}`,
  datosDeContacto: "Contatti",
  horario: "Orari",
  verEnElMapa: "Vedi sulla mappa",
  mapaCompleto: "Vedi la mappa intera",
  comoLlegar: "Come arrivare",
  activarMapa: "Clicca per muovere la mappa",
  otrosNegociosEn: (municipio) => `Altri locali a ${municipio}`,
  sinCartaTitulo: "Questo locale non ha ancora pubblicato il menu",
  sinCartaReclamada: "Il menu comparirà qui appena verrà pubblicato.",
  sinCartaLibre:
    "Se è il tuo locale, puoi rivendicare la scheda e pubblicare il menu gratis: i tuoi piatti e i tuoi prezzi compariranno in questa stessa pagina.",
  reclamarFicha: "Rivendica questa scheda",
  esTuNegocio:
    "È il tuo locale? Rivendica la scheda per correggere i dati e pubblicare il menu gratis.",
  verCartaCompleta: "Vedi il menu completo",
  cartaEtiqueta: "Menu",
  abiertoAhora: "Aperto ora",
  abiertosAhora: "Aperti ora",
  verTodosLosNegocios: "Vedi tutti",
  ningunoAbierto: "In questo momento non c’è niente aperto da queste parti.",
  cerradoAhora: "Chiuso ora",
  cerrado: "Chiuso",
  abierto24h: "Aperto 24 h",
  dias: ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"],
  traducidoAutomaticamente: "Tradotto automaticamente",
  servicios: "Servizi",
  formasDePago: "Metodi di pagamento",
  precio: "Prezzo",
  precioMedio: "Prezzo medio a piatto",
  precioDelPlato: (min, max) => `Da ${min} a ${max}`,
  platosEnLaCarta: (n) => `${n} piatti nel menu`,
  cartaEn: "Menu in",
  reservarMesa: "Si può prenotare il tavolo",
  pedirEnMesa: "Si può ordinare dal tavolo",
  enRedes: "Social",
  cartaEnOtraWeb: "Il suo menu su un altro sito",
  datoVerificado: (fecha) => `Dati verificati il ${fecha}`,
  valoraciones: "Valutazioni",
  popularidad: "Popolarità",
  michelin: (n) => `${n} ${n === 1 ? "stella" : "stelle"} Michelin`,
  popularAlta: "Molto frequentato",
  popularMedia: "Abbastanza frequentato",
  popularBaja: "Poco frequentato",
  segunPlataforma: (n) => `${n} valutazioni`,
  opinionesTitulo: "Com'è andata?",
  opinionesSubtitulo: "Valuta la tua esperienza",
  sinOpiniones: "Non ci sono ancora recensioni di questo locale.",
  fotosDeGoogle: "Foto di Google",
  recomendados: "Consigliati",
  faq: {
    titulo: "Domande frequenti",
    cocina: (n: string) => `Che cucina propone ${n}?`,
    precio: (n: string) => `Quanto costa mangiare da ${n}?`,
    terraza: (n: string) => `${n} ha una terrazza?`,
    accesible: (n: string) => `${n} è accessibile in sedia a rotelle?`,
    llevar: (n: string) => `Si può ordinare da asporto da ${n}?`,
    tarjeta: (n: string) => `Si può pagare con carta da ${n}?`,
    siTerraza: "Sì, ha una terrazza.",
    siAccesible: "Sì, l'ingresso è accessibile.",
    siLlevar: "Sì, è possibile ordinare da asporto.",
    siTarjeta: "Sì, accettano la carta.",
  },
  recomendadosCta: "Vuoi che il tuo locale compaia qui?",
  recomendadosPlan: "Passa al piano Professional",
  opinionesEnGoogle: "Recensioni su Google",
  leerEnGoogle: "Leggi su Google",
  verTodasEnGoogle: "Vedile tutte su Google Maps",
  resenasDeGoogleAviso: "Recensioni pubblicate su Google dai loro autori. Non sono recensioni di Zindra né si mescolano con esse.",
  iaPros: "Pro",
  iaContras: "Contro",
  reservar: "Prenota",
  reservarLlamando: (nombre) => `Chiama ${nombre} per prenotare`,
  pedirParaLlevar: "Ordina da asporto",
  llamarParaPedir: "Chiama il locale",
  sinPedirParaLlevar: (municipio) => `A ${municipio} non ci sono ordini da asporto con Glovo, Uber Eats o Just Eat`,
  iaTitulo: (nombre) => `Recensioni di ${nombre}: pro e contro`,
  iaSegun: (n) => `Riepilogo con IA di ${numero("it", n)} recensioni dei clienti`,
  iaAviso: "Riassunto scritto con intelligenza artificiale a partire dalle recensioni pubblicate su Google e Zindra. Può contenere errori: le recensioni sono più in basso.",
  iaActualizado: (mes) => `Aggiornato: ${mes}`,
  precioMuyBarato: "Molto economico",
  precioModerado: "Moderato",
  precioCaro: "Caro",
  precioBastanteCaro: "Piuttosto caro",
  precioPorPersona: (rango) => `${rango} a persona`,
  segunGoogle: "secondo Google",
  escribirOpinion: "Scrivi una recensione",
  respuestaDelNegocio: "Risposta del locale",
  respuestaEscritaEnZindra: "Risposta del locale, scritta su Zindra (non su Google)",
  opinionEnviada: "Grazie per la tua recensione",
  opinionEnRevision: "La controlliamo e la pubblichiamo entro 24 ore.",
  campoValoracion: "La tua valutazione",
  campoTitulo: "Titolo",
  campoOpinion: "La tua recensione",
  publicarOpinion: "Pubblica",
  avisoOsmAntes: "Parte dei contatti e della posizione proviene da ",
  avisoOsmDespues:
    ", con licenza ODbL. Se qualcosa non è corretto, il locale stesso può sistemarlo rivendicando la scheda.",
  sobreComerEn: (municipio) => `Mangiare a ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `Quanti bar e ristoranti ci sono a ${municipio}?`,
        respuesta: `La guida di Zindra riporta ${total.toLocaleString("it-IT")} locali a ${municipio} (${isla}): bar, ristoranti, caffetterie e cibo da asporto. Ognuno ha la sua scheda con indirizzo, telefono e orari.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `Che tipi di ristorante ci sono a ${municipio}?`,
        respuesta: `I più numerosi a ${municipio} sono ${unir(categorias.map((c) => `${c.cuantos} ${c.nombre}`), "e")}. Ogni tipo ha la sua pagina dentro la guida.`,
      });
    }
    preguntas.push({
      pregunta: "Si può vedere il menu prima di andare?",
      respuesta:
        conCarta > 0
          ? `Sì. ${conCarta === 1 ? "Un locale" : `${conCarta} locali`} di ${municipio} ${conCarta === 1 ? "ha" : "hanno"} pubblicato il menu, con piatti e prezzi, e si legge dal telefono senza installare niente.`
          : `A ${municipio} nessun locale ha ancora pubblicato il menu. Chi lo farà mostrerà piatti e prezzi nella sua scheda.`,
    });
    preguntas.push({
      pregunta: `Ho un bar a ${municipio}, come compaio qui?`,
      respuesta: `Creando il tuo menu digitale su Zindra, che è gratis, e pubblicandolo nella guida. Se il tuo locale è già in lista, puoi rivendicarne la scheda da lì. Più è completa, più in alto compare in questa pagina.`,
    });
    return preguntas;
  },
};

const nl: Textos = {
  directorio: "Restaurantgids",
  guiaDe: (isla) => `Restaurantgids ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} bars en restaurants in ${municipios} gemeenten, met openingstijden, de menukaart als die er is en wat bezoekers ervan vinden.`,
  dondeComerEn: (lugar) => `Waar eet je goed op ${lugar}`,
  todosLosMunicipios: "Alle gemeenten",
  queTeApetece: "Waar heb je zin in?",
  queTeApeteceEn: (lugar) => `Waar heb je zin in, in ${lugar}?`,
  todosLosTipos: "Alle keukens en soorten zaken",
  masOpciones: "Meer soorten en voorzieningen",
  elegirMunicipio: "Kies een gemeente",
  masPopularesEn: (lugar) => `Populairste zaken op ${lugar}`,
  masPopularesNota: "Gerangschikt op aantal beoordelingen.",
  planes: "Naar gelegenheid",
  verTodos: "Alles bekijken",
  buscar: "Zoeken",
  buscarTitulo: "Zoeken in de restaurantgids",
  buscarPlaceholder: "Zoek een bar, een restaurant of een gemeente",
  buscarBoton: "Zoeken",
  buscarSinResultados: "Er is geen zaak met die naam.",
  buscarDaloDeAlta: "Is het uw zaak? U kunt hem gratis aanmelden.",
  municipios: "Gemeenten",
  negocios: "Zaken",
  porTipoDeNegocio: "Per soort zaak",
  porCaracteristica: "Op voorzieningen",
  porMunicipio: "Per gemeente",
  portadaTitulo: "Gids van bars en restaurants",
  portadaIntro:
    "De horecazaken van elke gemeente, met adres, openingstijden en, als die gepubliceerd is, hun menukaart met prijzen.",
  portadaCtaTitulo: "Heeft u een bar of een restaurant?",
  portadaCtaTexto:
    "Maak uw digitale menukaart gratis aan en publiceer hem hier. Uw zaak verschijnt in de gids van uw gemeente met uw gerechten en uw prijzen, en uw gasten lezen hem voordat ze binnenkomen.",
  ctaBoton: "Mijn menukaart gratis publiceren",
  altaTitulo: "Staat uw zaak er niet bij?",
  altaTexto:
    "Meld hem gratis aan: wij controleren hem, publiceren hem in de gids en daarna kunt u uw menukaart uploaden.",
  altaBoton: "Mijn zaak aanmelden",
  ctaMunicipioTitulo: "Ontbreekt uw zaak, of wilt u uw menukaart publiceren?",
  ctaMunicipioTexto:
    "Maak uw digitale menukaart gratis aan met Zindra en hij verschijnt op deze pagina, met uw gerechten, uw prijzen en actuele openingstijden.",
  ctaIslaTitulo: (isla) => `Ligt uw zaak op ${isla}?`,
  ctaIslaTexto:
    "Publiceer uw menukaart gratis en verschijn in de gids van uw gemeente, met uw gerechten en uw prijzen.",
  ctaCategoriaTitulo: (categoria, isla) => `Heeft u ${categoria} op ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Bars en restaurants in ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} horecazaken verspreid over ${municipios} gemeenten. Kies de uwe om de bars, restaurants en cafés in de buurt te zien, met adres en menukaart.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} horecazaken in ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Wie zijn menukaart gepubliceerd heeft, toont hier gerechten en prijzen." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} in ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} ${categoria} in ${lugar}, met adres, openingstijden en, indien gepubliceerd, de menukaart met prijzen.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} bars, restaurants en cafés in ${municipio} (${isla}): adres, openingstijden, telefoon en menukaart met prijzen.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} ${categoria} in ${lugar}: adres, openingstijden, telefoon en menukaart met prijzen.`,
  descripcionIsla: (isla) =>
    `Gids van bars, restaurants en cafés op ${isla}, gemeente voor gemeente, met adres, openingstijden en menukaart.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} in ${municipio}${direccion ? ` (${direccion})` : ""}. Openingstijden, telefoon, locatie en menukaart.`,
  negociosCuenta: (n) => `${n} zaken`,
  paginaXdeY: (x, y) => `Zaken (pagina ${x} van ${y})`,
  anterior: "Vorige",
  cerrar: "Sluiten",
  siguiente: "Volgende",
  paginas: "Pagina's",
  ruta: "Kruimelpad",
  verTodosEn: (municipio) => `Alle bars en restaurants in ${municipio} bekijken`,
  negocioDeHosteleria: "Horecazaak",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} in ${municipio}, ${isla}`,
  datosDeContacto: "Contactgegevens",
  horario: "Openingstijden",
  verEnElMapa: "Op de kaart bekijken",
  mapaCompleto: "Grotere kaart bekijken",
  comoLlegar: "Route",
  activarMapa: "Klik om de kaart te verplaatsen",
  otrosNegociosEn: (municipio) => `Andere zaken in ${municipio}`,
  sinCartaTitulo: "Deze zaak heeft haar menukaart nog niet gepubliceerd",
  sinCartaReclamada: "De menukaart verschijnt hier zodra hij gepubliceerd wordt.",
  sinCartaLibre:
    "Is dit uw zaak? U kunt de vermelding claimen en uw menukaart gratis publiceren: uw gerechten en uw prijzen komen op deze pagina te staan.",
  reclamarFicha: "Deze vermelding claimen",
  esTuNegocio:
    "Is dit uw zaak? Claim de vermelding om de gegevens te corrigeren en uw menukaart gratis te publiceren.",
  verCartaCompleta: "Volledige menukaart bekijken",
  cartaEtiqueta: "Menukaart",
  abiertoAhora: "Nu open",
  abiertosAhora: "Nu open",
  verTodosLosNegocios: "Alles bekijken",
  ningunoAbierto: "Er is hier nu niets open.",
  cerradoAhora: "Nu gesloten",
  cerrado: "Gesloten",
  abierto24h: "24 uur open",
  dias: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"],
  traducidoAutomaticamente: "Automatisch vertaald",
  servicios: "Voorzieningen",
  formasDePago: "Betaalmethoden",
  precio: "Prijs",
  precioMedio: "Gemiddelde prijs per gerecht",
  precioDelPlato: (min, max) => `Van ${min} tot ${max}`,
  platosEnLaCarta: (n) => `${n} gerechten op de kaart`,
  cartaEn: "Menukaart in",
  reservarMesa: "Tafel reserveren mogelijk",
  pedirEnMesa: "Bestellen aan tafel mogelijk",
  enRedes: "Social media",
  cartaEnOtraWeb: "Hun menukaart op een andere site",
  datoVerificado: (fecha) => `Gegevens gecontroleerd op ${fecha}`,
  valoraciones: "Beoordelingen",
  popularidad: "Populariteit",
  michelin: (n) => `${n} Michelinster${n === 1 ? "" : "ren"}`,
  popularAlta: "Zeer druk bezocht",
  popularMedia: "Goed bezocht",
  popularBaja: "Weinig bezocht",
  segunPlataforma: (n) => `${n} beoordelingen`,
  opinionesTitulo: "Hoe was het?",
  opinionesSubtitulo: "Beoordeel uw ervaring",
  sinOpiniones: "Er zijn nog geen recensies van deze zaak.",
  fotosDeGoogle: "Foto's van Google",
  recomendados: "Aanbevolen",
  faq: {
    titulo: "Veelgestelde vragen",
    cocina: (n: string) => `Wat voor keuken heeft ${n}?`,
    precio: (n: string) => `Wat kost eten bij ${n}?`,
    terraza: (n: string) => `Heeft ${n} een terras?`,
    accesible: (n: string) => `Is ${n} rolstoeltoegankelijk?`,
    llevar: (n: string) => `Kun je bij ${n} afhalen?`,
    tarjeta: (n: string) => `Kun je bij ${n} met kaart betalen?`,
    siTerraza: "Ja, met terras.",
    siAccesible: "Ja, de ingang is toegankelijk.",
    siLlevar: "Ja, afhalen kan.",
    siTarjeta: "Ja, kaartbetaling wordt geaccepteerd.",
  },
  recomendadosCta: "Wil je dat jouw zaak hier verschijnt?",
  recomendadosPlan: "Neem het Professional-abonnement",
  opinionesEnGoogle: "Recensies op Google",
  leerEnGoogle: "Lees op Google",
  verTodasEnGoogle: "Bekijk ze allemaal op Google Maps",
  resenasDeGoogleAviso: "Recensies die door hun auteurs op Google zijn geplaatst. Het zijn geen Zindra-recensies en ze worden er niet mee vermengd.",
  iaPros: "Pluspunten",
  iaContras: "Minpunten",
  reservar: "Reserveren",
  reservarLlamando: (nombre) => `Bel ${nombre} om te reserveren`,
  pedirParaLlevar: "Afhaal bestellen",
  llamarParaPedir: "Het restaurant bellen",
  sinPedirParaLlevar: (municipio) => `Geen afhaalbestellingen via Glovo, Uber Eats of Just Eat in ${municipio}`,
  iaTitulo: (nombre) => `${nombre} ervaringen: plus- en minpunten`,
  iaSegun: (n) => `AI-samenvatting van ${numero("nl", n)} klantrecensies`,
  iaAviso: "Samenvatting gemaakt met kunstmatige intelligentie op basis van de recensies op Google en Zindra. Er kunnen fouten in staan: de recensies staan verderop.",
  iaActualizado: (mes) => `Bijgewerkt: ${mes}`,
  precioMuyBarato: "Heel goedkoop",
  precioModerado: "Gemiddeld",
  precioCaro: "Duur",
  precioBastanteCaro: "Vrij duur",
  precioPorPersona: (rango) => `${rango} per persoon`,
  segunGoogle: "volgens Google",
  escribirOpinion: "Een recensie schrijven",
  respuestaDelNegocio: "Reactie van de zaak",
  respuestaEscritaEnZindra: "Reactie van de zaak, geschreven op Zindra (niet op Google)",
  opinionEnviada: "Bedankt voor uw recensie",
  opinionEnRevision: "We controleren hem en plaatsen hem binnen 24 uur.",
  campoValoracion: "Uw beoordeling",
  campoTitulo: "Titel",
  campoOpinion: "Uw recensie",
  publicarOpinion: "Plaatsen",
  avisoOsmAntes: "Een deel van de contact- en locatiegegevens komt van ",
  avisoOsmDespues:
    ", onder ODbL-licentie. Klopt er iets niet, dan kan de zaak het zelf corrigeren zodra ze haar vermelding claimt.",
  sobreComerEn: (municipio) => `Uit eten in ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `Hoeveel bars en restaurants zijn er in ${municipio}?`,
        respuesta: `In de gids van Zindra staan ${total.toLocaleString("nl-NL")} horecazaken in ${municipio} (${isla}): bars, restaurants, cafés en afhaalzaken. Elke zaak heeft haar vermelding met adres, telefoon en openingstijden.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `Welke soorten restaurants zijn er in ${municipio}?`,
        respuesta: `Het talrijkst in ${municipio} zijn ${unir(categorias.map((c) => `${c.cuantos} ${c.nombre}`), "en")}. Elk soort heeft zijn eigen pagina in de gids.`,
      });
    }
    preguntas.push({
      pregunta: "Kan ik de menukaart vooraf bekijken?",
      respuesta:
        conCarta > 0
          ? `Ja. ${conCarta === 1 ? "Eén zaak" : `${conCarta} zaken`} in ${municipio} ${conCarta === 1 ? "heeft" : "hebben"} de menukaart gepubliceerd, met gerechten en prijzen, te lezen op de telefoon zonder iets te installeren.`
          : `Nog geen enkele zaak in ${municipio} heeft haar menukaart gepubliceerd. Wie dat doet, toont gerechten en prijzen in de vermelding.`,
    });
    preguntas.push({
      pregunta: `Ik heb een bar in ${municipio}. Hoe kom ik hier in?`,
      respuesta: `Maak uw digitale menukaart aan bij Zindra — dat is gratis — en zet hem open voor de gids. Staat uw zaak al in de lijst, dan kunt u de vermelding daar meteen claimen. Hoe vollediger die is, hoe hoger hij op deze pagina staat.`,
    });
    return preguntas;
  },
};

const pl: Textos = {
  directorio: "Przewodnik po restauracjach",
  guiaDe: (isla) => `Przewodnik po restauracjach: ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} barów i restauracji w ${municipios} gminach, z godzinami otwarcia, menu, jeśli je mają, i opiniami tych, którzy już tam byli.`,
  dondeComerEn: (lugar) => `Gdzie zjeść: ${lugar}`,
  todosLosMunicipios: "Wszystkie gminy",
  queTeApetece: "Na co masz ochotę?",
  queTeApeteceEn: (lugar) => `Na co masz ochotę? ${lugar}`,
  todosLosTipos: "Wszystkie kuchnie i rodzaje lokali",
  masOpciones: "Więcej rodzajów i udogodnień",
  elegirMunicipio: "Wybierz gminę",
  masPopularesEn: (lugar) => `Najpopularniejsze: ${lugar}`,
  masPopularesNota: "Uporządkowane według liczby opinii.",
  planes: "Według okazji",
  verTodos: "Zobacz wszystkie",
  buscar: "Szukaj",
  buscarTitulo: "Szukaj w przewodniku",
  buscarPlaceholder: "Znajdź bar, restaurację lub gminę",
  buscarBoton: "Szukaj",
  buscarSinResultados: "Nie ma lokalu o takiej nazwie.",
  buscarDaloDeAlta: "Jeśli to Twój lokal, możesz go dodać za darmo.",
  municipios: "Gminy",
  negocios: "Lokale",
  porTipoDeNegocio: "Według rodzaju lokalu",
  porCaracteristica: "Według udogodnień",
  porMunicipio: "Według gminy",
  portadaTitulo: "Przewodnik po barach i restauracjach",
  portadaIntro:
    "Lokale gastronomiczne w każdej miejscowości, z adresem, godzinami otwarcia oraz — jeśli je opublikowały — menu z cenami.",
  portadaCtaTitulo: "Masz bar albo restaurację?",
  portadaCtaTexto:
    "Stwórz cyfrowe menu za darmo i opublikuj je tutaj. Twój lokal pojawi się w przewodniku Twojej miejscowości z Twoimi daniami i cenami, a goście przeczytają je, zanim wejdą.",
  ctaBoton: "Opublikuj moje menu za darmo",
  altaTitulo: "Nie ma tu Twojego lokalu?",
  altaTexto:
    "Dodaj go za darmo: sprawdzimy zgłoszenie, opublikujemy wizytówkę w przewodniku, a potem wgrasz swoje menu.",
  altaBoton: "Dodaj mój lokal",
  ctaMunicipioTitulo: "Brakuje Twojego lokalu albo chcesz opublikować menu?",
  ctaMunicipioTexto:
    "Stwórz cyfrowe menu za darmo w Zindrze, a pojawi się na tej stronie — z Twoimi daniami, cenami i aktualnymi godzinami otwarcia.",
  ctaIslaTitulo: (isla) => `Twój lokal jest na ${isla}?`,
  ctaIslaTexto:
    "Opublikuj menu za darmo i pojaw się w przewodniku swojej gminy, z daniami i cenami.",
  ctaCategoriaTitulo: (categoria, isla) => `Prowadzisz ${categoria} na ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Bary i restauracje w ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} lokali gastronomicznych w ${municipios} gminach. Wybierz swoją, żeby zobaczyć bary, restauracje i kawiarnie w okolicy razem z adresem i menu.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} lokali gastronomicznych w ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Te, które opublikowały menu, pokazują dania i ceny." : ""),
  categoriaEn: (categoria, lugar) => `${categoria} w ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} — ${categoria} w ${lugar}, z adresem, godzinami otwarcia i, jeśli zostało opublikowane, menu z cenami.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} barów, restauracji i kawiarni w ${municipio} (${isla}): adres, godziny otwarcia, telefon i menu z cenami.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} — ${categoria} w ${lugar}: adres, godziny otwarcia, telefon i menu z cenami.`,
  descripcionIsla: (isla) =>
    `Przewodnik po barach, restauracjach i kawiarniach na ${isla}, gmina po gminie, z adresem, godzinami otwarcia i menu.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} w ${municipio}${direccion ? ` (${direccion})` : ""}. Godziny otwarcia, telefon, lokalizacja i menu.`,
  negociosCuenta: (n) => `${n} lokali`,
  paginaXdeY: (x, y) => `Lokale (strona ${x} z ${y})`,
  anterior: "Poprzednia",
  cerrar: "Zamknij",
  siguiente: "Następna",
  paginas: "Strony",
  ruta: "Ścieżka",
  verTodosEn: (municipio) => `Zobacz wszystkie bary i restauracje w ${municipio}`,
  negocioDeHosteleria: "Lokal gastronomiczny",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} w ${municipio}, ${isla}`,
  datosDeContacto: "Kontakt",
  horario: "Godziny otwarcia",
  verEnElMapa: "Zobacz na mapie",
  mapaCompleto: "Zobacz pełną mapę",
  comoLlegar: "Jak dojechać",
  activarMapa: "Kliknij, aby przesunąć mapę",
  otrosNegociosEn: (municipio) => `Inne lokale w ${municipio}`,
  sinCartaTitulo: "Ten lokal nie opublikował jeszcze swojego menu",
  sinCartaReclamada: "Menu pojawi się tutaj, gdy tylko zostanie opublikowane.",
  sinCartaLibre:
    "Jeśli to Twój lokal, możesz przejąć wizytówkę i opublikować menu za darmo: Twoje dania i ceny pojawią się na tej samej stronie.",
  reclamarFicha: "Przejmij tę wizytówkę",
  esTuNegocio:
    "To Twój lokal? Przejmij wizytówkę, żeby poprawić dane i opublikować menu za darmo.",
  verCartaCompleta: "Zobacz pełne menu",
  cartaEtiqueta: "Menu",
  abiertoAhora: "Teraz otwarte",
  abiertosAhora: "Otwarte teraz",
  verTodosLosNegocios: "Zobacz wszystkie",
  ningunoAbierto: "W tej chwili nic tu nie jest otwarte.",
  cerradoAhora: "Teraz zamknięte",
  cerrado: "Zamknięte",
  abierto24h: "Otwarte całą dobę",
  dias: ["Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota", "Niedziela"],
  traducidoAutomaticamente: "Przetłumaczone automatycznie",
  servicios: "Udogodnienia",
  formasDePago: "Formy płatności",
  precio: "Cena",
  precioMedio: "Średnia cena dania",
  precioDelPlato: (min, max) => `Od ${min} do ${max}`,
  platosEnLaCarta: (n) => `${n} dań w menu`,
  cartaEn: "Menu w językach",
  reservarMesa: "Można zarezerwować stolik",
  pedirEnMesa: "Można zamówić przy stoliku",
  enRedes: "Media społecznościowe",
  cartaEnOtraWeb: "Ich menu na innej stronie",
  datoVerificado: (fecha) => `Dane zweryfikowane ${fecha}`,
  valoraciones: "Oceny",
  popularidad: "Popularność",
  michelin: (n) => `${n} ${n === 1 ? "gwiazdka" : "gwiazdki"} Michelin`,
  popularAlta: "Bardzo popularne",
  popularMedia: "Chętnie odwiedzane",
  popularBaja: "Rzadko odwiedzane",
  segunPlataforma: (n) => `${n} ocen`,
  opinionesTitulo: "Jak było?",
  opinionesSubtitulo: "Oceń swoją wizytę",
  sinOpiniones: "Nie ma jeszcze opinii o tym lokalu.",
  fotosDeGoogle: "Zdjęcia z Google",
  recomendados: "Polecane",
  faq: {
    titulo: "Najczęstsze pytania",
    cocina: (n: string) => `Jaką kuchnię serwuje ${n}?`,
    precio: (n: string) => `Ile kosztuje posiłek w ${n}?`,
    terraza: (n: string) => `Czy ${n} ma taras?`,
    accesible: (n: string) => `Czy ${n} jest dostępny dla wózków?`,
    llevar: (n: string) => `Czy w ${n} można zamówić na wynos?`,
    tarjeta: (n: string) => `Czy w ${n} można zapłacić kartą?`,
    siTerraza: "Tak, jest taras.",
    siAccesible: "Tak, wejście jest dostępne.",
    siLlevar: "Tak, można zamówić na wynos.",
    siTarjeta: "Tak, akceptują karty.",
  },
  recomendadosCta: "Chcesz, aby Twój lokal pojawił się tutaj?",
  recomendadosPlan: "Wykup plan Professional",
  opinionesEnGoogle: "Opinie w Google",
  leerEnGoogle: "Przeczytaj w Google",
  verTodasEnGoogle: "Zobacz wszystkie w Mapach Google",
  resenasDeGoogleAviso: "Opinie opublikowane w Google przez ich autorów. Nie są to opinie Zindry i nie są z nimi łączone.",
  iaPros: "Zalety",
  iaContras: "Wady",
  reservar: "Zarezerwuj",
  reservarLlamando: (nombre) => `Zadzwoń do ${nombre}, aby zarezerwować`,
  pedirParaLlevar: "Zamów na wynos",
  llamarParaPedir: "Zadzwoń do lokalu",
  sinPedirParaLlevar: (municipio) => `Brak zamówień na wynos przez Glovo, Uber Eats i Just Eat (${municipio})`,
  iaTitulo: (nombre) => `${nombre} opinie: zalety i wady`,
  iaSegun: (n) => `Podsumowanie AI na podstawie ${numero("pl", n)} opinii klientów`,
  iaAviso: "Podsumowanie przygotowane przez sztuczną inteligencję na podstawie opinii opublikowanych w Google i w Zindrze. Może zawierać błędy: opinie znajdują się niżej.",
  iaActualizado: (mes) => `Zaktualizowano: ${mes}`,
  precioMuyBarato: "Bardzo tanio",
  precioModerado: "Umiarkowanie",
  precioCaro: "Drogo",
  precioBastanteCaro: "Dość drogo",
  precioPorPersona: (rango) => `${rango} na osobę`,
  segunGoogle: "według Google",
  escribirOpinion: "Napisz opinię",
  respuestaDelNegocio: "Odpowiedź lokalu",
  respuestaEscritaEnZindra: "Odpowiedź lokalu, napisana w Zindrze (nie w Google)",
  opinionEnviada: "Dziękujemy za opinię",
  opinionEnRevision: "Sprawdzamy ją i publikujemy w ciągu 24 godzin.",
  campoValoracion: "Twoja ocena",
  campoTitulo: "Tytuł",
  campoOpinion: "Twoja opinia",
  publicarOpinion: "Opublikuj",
  avisoOsmAntes: "Część danych kontaktowych i lokalizacji pochodzi z ",
  avisoOsmDespues:
    ", na licencji ODbL. Jeśli coś się nie zgadza, lokal może to poprawić, przejmując swoją wizytówkę.",
  sobreComerEn: (municipio) => `Gdzie zjeść w ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `Ile jest barów i restauracji w ${municipio}?`,
        respuesta: `W przewodniku Zindry jest ${total.toLocaleString("pl-PL")} lokali gastronomicznych w ${municipio} (${isla}): bary, restauracje, kawiarnie i lokale na wynos. Każdy ma swoją wizytówkę z adresem, telefonem i godzinami otwarcia.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `Jakie rodzaje restauracji są w ${municipio}?`,
        respuesta: `Najliczniejsze w ${municipio} są ${unir(categorias.map((c) => `${c.cuantos} — ${c.nombre}`), "oraz")}. Każdy rodzaj ma własną stronę w przewodniku.`,
      });
    }
    preguntas.push({
      pregunta: "Czy można zobaczyć menu przed wizytą?",
      respuesta:
        conCarta > 0
          ? `Tak. ${conCarta === 1 ? "Jeden lokal" : `${conCarta} lokali`} w ${municipio} opublikował${conCarta === 1 ? "" : "o"} menu z daniami i cenami — otwiera się na telefonie, bez instalowania czegokolwiek.`
          : `Żaden lokal w ${municipio} nie opublikował jeszcze menu. Te, które to zrobią, pokażą dania i ceny na swojej wizytówce.`,
    });
    preguntas.push({
      pregunta: `Mam bar w ${municipio}. Jak się tu znaleźć?`,
      respuesta: `Załóż cyfrowe menu w Zindrze — to nic nie kosztuje — i zaznacz, żeby było widoczne w przewodniku. Jeśli Twój lokal już jest na liście, możesz przejąć jego wizytówkę prosto z niej. Im pełniejsza, tym wyżej na tej stronie.`,
    });
    return preguntas;
  },
};

const ru: Textos = {
  directorio: "Гид по ресторанам",
  guiaDe: (isla) => `Гид по ресторанам: ${isla}`,
  guiaIntro: (total, municipios) =>
    `${total} баров и ресторанов в ${municipios} муниципалитетах: часы работы, меню, если оно есть, и отзывы тех, кто там уже был.`,
  dondeComerEn: (lugar) => `Где поесть: ${lugar}`,
  todosLosMunicipios: "Все муниципалитеты",
  queTeApetece: "Чего хочется?",
  queTeApeteceEn: (lugar) => `Чего хочется? ${lugar}`,
  todosLosTipos: "Все кухни и типы заведений",
  masOpciones: "Другие типы и услуги",
  elegirMunicipio: "Выберите муниципалитет",
  masPopularesEn: (lugar) => `Самые популярные: ${lugar}`,
  masPopularesNota: "По количеству отзывов.",
  planes: "По случаю",
  verTodos: "Смотреть все",
  buscar: "Поиск",
  buscarTitulo: "Поиск по гиду",
  buscarPlaceholder: "Найдите бар, ресторан или муниципалитет",
  buscarBoton: "Найти",
  buscarSinResultados: "Заведения с таким названием нет.",
  buscarDaloDeAlta: "Если это ваше заведение, добавьте его бесплатно.",
  municipios: "Муниципалитеты",
  negocios: "Заведения",
  porTipoDeNegocio: "По типу заведения",
  porCaracteristica: "По удобствам",
  porMunicipio: "По муниципалитету",
  portadaTitulo: "Гид по барам и ресторанам",
  portadaIntro:
    "Заведения общественного питания каждого населённого пункта: адрес, часы работы и, если оно опубликовано, меню с ценами.",
  portadaCtaTitulo: "У вас бар или ресторан?",
  portadaCtaTexto:
    "Создайте цифровое меню бесплатно и опубликуйте его здесь. Ваше заведение появится в гиде по вашему населённому пункту с блюдами и ценами, и гости увидят меню ещё до входа.",
  ctaBoton: "Опубликовать меню бесплатно",
  altaTitulo: "Вашего заведения здесь нет?",
  altaTexto:
    "Добавьте его бесплатно: мы проверим заявку, опубликуем страницу в гиде, а дальше вы сможете загрузить меню.",
  altaBoton: "Добавить заведение",
  ctaMunicipioTitulo: "Вашего заведения здесь нет или хотите опубликовать меню?",
  ctaMunicipioTexto:
    "Создайте цифровое меню в Zindra бесплатно — оно появится на этой странице вместе с блюдами, ценами и актуальными часами работы.",
  ctaIslaTitulo: (isla) => `Ваше заведение на ${isla}?`,
  ctaIslaTexto:
    "Опубликуйте меню бесплатно и появитесь в гиде по своему муниципалитету с блюдами и ценами.",
  ctaCategoriaTitulo: (categoria, isla) => `У вас ${categoria} на ${isla}?`,
  baresYRestaurantesEn: (lugar) => `Бары и рестораны: ${lugar}`,
  islaIntro: (total, municipios) =>
    `${total} заведений в ${municipios} муниципалитетах. Выберите свой, чтобы увидеть бары, рестораны и кофейни района с адресом и меню.`,
  municipioIntro: (total, municipio, isla, conCarta) =>
    `${total} заведений общественного питания: ${municipio}${municipio !== isla ? `, ${isla}` : ""}.` +
    (conCarta > 0 ? " Те, кто опубликовал меню, показывают блюда и цены." : ""),
  categoriaEn: (categoria, lugar) => `${categoria}: ${lugar}`,
  categoriaIntro: (total, categoria, lugar) =>
    `${total} — ${categoria}, ${lugar}. Адрес, часы работы и, если опубликовано, меню с ценами.`,
  descripcionMunicipio: (total, municipio, isla) =>
    `${total} баров, ресторанов и кофеен в ${municipio} (${isla}): адрес, часы работы, телефон и меню с ценами.`,
  descripcionCategoria: (total, categoria, lugar) =>
    `${total} — ${categoria}, ${lugar}: адрес, часы работы, телефон и меню с ценами.`,
  descripcionIsla: (isla) =>
    `Гид по барам, ресторанам и кофейням ${isla} по муниципалитетам: адрес, часы работы и меню.`,
  descripcionFicha: (nombre, tipo, municipio, direccion) =>
    `${nombre}, ${tipo} в ${municipio}${direccion ? ` (${direccion})` : ""}. Часы работы, телефон, расположение и меню.`,
  negociosCuenta: (n) => `${n} заведений`,
  paginaXdeY: (x, y) => `Заведения (страница ${x} из ${y})`,
  anterior: "Назад",
  cerrar: "Закрыть",
  siguiente: "Вперёд",
  paginas: "Страницы",
  ruta: "Навигация",
  verTodosEn: (municipio) => `Все бары и рестораны: ${municipio}`,
  negocioDeHosteleria: "Заведение общественного питания",
  tipoEnLugar: (tipo, municipio, isla) => `${tipo} в ${municipio}, ${isla}`,
  datosDeContacto: "Контакты",
  horario: "Часы работы",
  verEnElMapa: "Посмотреть на карте",
  mapaCompleto: "Открыть карту целиком",
  comoLlegar: "Как добраться",
  activarMapa: "Нажмите, чтобы двигать карту",
  otrosNegociosEn: (municipio) => `Другие заведения: ${municipio}`,
  sinCartaTitulo: "Это заведение ещё не опубликовало меню",
  sinCartaReclamada: "Меню появится здесь, как только его опубликуют.",
  sinCartaLibre:
    "Если это ваше заведение, подтвердите права на карточку и опубликуйте меню бесплатно: ваши блюда и цены появятся прямо на этой странице.",
  reclamarFicha: "Это моё заведение",
  esTuNegocio:
    "Это ваше заведение? Подтвердите права на карточку, чтобы исправить данные и бесплатно опубликовать меню.",
  verCartaCompleta: "Посмотреть меню целиком",
  cartaEtiqueta: "Меню",
  abiertoAhora: "Сейчас открыто",
  abiertosAhora: "Открыто сейчас",
  verTodosLosNegocios: "Показать все",
  ningunoAbierto: "Сейчас поблизости ничего не открыто.",
  cerradoAhora: "Сейчас закрыто",
  cerrado: "Закрыто",
  abierto24h: "Круглосуточно",
  dias: ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"],
  traducidoAutomaticamente: "Автоматический перевод",
  servicios: "Услуги",
  formasDePago: "Способы оплаты",
  precio: "Цены",
  precioMedio: "Средняя цена блюда",
  precioDelPlato: (min, max) => `От ${min} до ${max}`,
  platosEnLaCarta: (n) => `${n} блюд в меню`,
  cartaEn: "Меню на языках",
  reservarMesa: "Можно забронировать столик",
  pedirEnMesa: "Можно заказать за столиком",
  enRedes: "Соцсети",
  cartaEnOtraWeb: "Их меню на другом сайте",
  datoVerificado: (fecha) => `Данные проверены ${fecha}`,
  valoraciones: "Оценки",
  popularidad: "Популярность",
  michelin: (n) => `${n} ${n === 1 ? "звезда" : "звезды"} Мишлен`,
  popularAlta: "Очень популярно",
  popularMedia: "Часто посещают",
  popularBaja: "Редко посещают",
  segunPlataforma: (n) => `${n} оценок`,
  opinionesTitulo: "Как вам?",
  opinionesSubtitulo: "Оцените свои впечатления",
  sinOpiniones: "Отзывов об этом заведении пока нет.",
  fotosDeGoogle: "Фотографии из Google",
  recomendados: "Рекомендуемые",
  faq: {
    titulo: "Частые вопросы",
    cocina: (n: string) => `Какая кухня в ${n}?`,
    precio: (n: string) => `Сколько стоит поесть в ${n}?`,
    terraza: (n: string) => `Есть ли терраса в ${n}?`,
    accesible: (n: string) => `Доступен ли ${n} для колясок?`,
    llevar: (n: string) => `Можно ли заказать навынос в ${n}?`,
    tarjeta: (n: string) => `Можно ли оплатить картой в ${n}?`,
    siTerraza: "Да, есть терраса.",
    siAccesible: "Да, вход доступен.",
    siLlevar: "Да, есть заказ навынос.",
    siTarjeta: "Да, карты принимают.",
  },
  recomendadosCta: "Хотите, чтобы ваше заведение было здесь?",
  recomendadosPlan: "Оформить план Professional",
  opinionesEnGoogle: "Отзывы в Google",
  leerEnGoogle: "Читать в Google",
  verTodasEnGoogle: "Смотреть все в Google Картах",
  resenasDeGoogleAviso: "Отзывы, опубликованные в Google их авторами. Это не отзывы Zindra, и они с ними не смешиваются.",
  iaPros: "Плюсы",
  iaContras: "Минусы",
  reservar: "Забронировать",
  reservarLlamando: (nombre) => `Позвонить в ${nombre}, чтобы забронировать`,
  pedirParaLlevar: "Заказать с собой",
  llamarParaPedir: "Позвонить в заведение",
  sinPedirParaLlevar: (municipio) => `Заказ с собой через Glovo, Uber Eats и Just Eat недоступен (${municipio})`,
  iaTitulo: (nombre) => `${nombre}: отзывы, плюсы и минусы`,
  iaSegun: (n) =>
    `Сводка ИИ по ${numero("ru", n)} ${n % 10 === 1 && n % 100 !== 11 ? "отзыву" : "отзывам"} гостей`,
  iaAviso: "Сводка составлена искусственным интеллектом по отзывам, опубликованным в Google и Zindra. В ней возможны ошибки: сами отзывы ниже.",
  iaActualizado: (mes) => `Обновлено: ${mes}`,
  precioMuyBarato: "Очень дёшево",
  precioModerado: "Умеренно",
  precioCaro: "Дорого",
  precioBastanteCaro: "Довольно дорого",
  precioPorPersona: (rango) => `${rango} на человека`,
  segunGoogle: "по данным Google",
  escribirOpinion: "Написать отзыв",
  respuestaDelNegocio: "Ответ заведения",
  respuestaEscritaEnZindra: "Ответ заведения, написанный в Zindra (не в Google)",
  opinionEnviada: "Спасибо за отзыв",
  opinionEnRevision: "Мы проверим его и опубликуем в течение 24 часов.",
  campoValoracion: "Ваша оценка",
  campoTitulo: "Заголовок",
  campoOpinion: "Ваш отзыв",
  publicarOpinion: "Опубликовать",
  avisoOsmAntes: "Часть контактных данных и координат взята из ",
  avisoOsmDespues:
    " по лицензии ODbL. Если что-то указано неверно, заведение может исправить это, подтвердив права на свою карточку.",
  sobreComerEn: (municipio) => `Где поесть: ${municipio}`,
  preguntas: ({ municipio, isla, total, conCarta, categorias }) => {
    if (total === 0) return [];
    const preguntas: Pregunta[] = [
      {
        pregunta: `Сколько баров и ресторанов в ${municipio}?`,
        respuesta: `В гиде Zindra ${total.toLocaleString("ru-RU")} заведений общественного питания в ${municipio} (${isla}): бары, рестораны, кофейни и еда навынос. У каждого своя карточка с адресом, телефоном и часами работы.`,
      },
    ];
    if (categorias.length > 0) {
      preguntas.push({
        pregunta: `Какие рестораны есть в ${municipio}?`,
        respuesta: `Больше всего в ${municipio} таких заведений: ${unir(categorias.map((c) => `${c.cuantos} — ${c.nombre}`), "и")}. У каждого типа своя страница в гиде.`,
      });
    }
    preguntas.push({
      pregunta: "Можно ли посмотреть меню заранее?",
      respuesta:
        conCarta > 0
          ? `Да. ${conCarta === 1 ? "Одно заведение" : `${conCarta} заведений`} в ${municipio} опубликовало меню с блюдами и ценами — оно открывается на телефоне, ничего устанавливать не нужно.`
          : `Пока ни одно заведение в ${municipio} не опубликовало меню. Те, кто это сделает, покажут блюда и цены в своей карточке.`,
    });
    preguntas.push({
      pregunta: `У меня бар в ${municipio}. Как сюда попасть?`,
      respuesta: `Создайте цифровое меню в Zindra — это бесплатно — и отметьте его для показа в гиде. Если ваше заведение уже есть в списке, права на карточку можно подтвердить прямо оттуда. Чем полнее карточка, тем выше она на этой странице.`,
    });
    return preguntas;
  },
};

/** Todos los idiomas. El tipo obliga a que ninguno se quede a medias. */
export const TEXTOS: Record<Idioma, Textos> = { es, de, en, ca, fr, it, nl, pl, ru };

export function textos(idioma: Idioma): Textos {
  return TEXTOS[idioma];
}

/** Una nota con un decimal, en la coma o el punto que use el idioma. */
export function conDecimal(idioma: Idioma, valor: number): string {
  const etiquetas: Record<Idioma, string> = {
    es: "es-ES", de: "de-DE", en: "en-GB", ca: "ca-ES", fr: "fr-FR",
    it: "it-IT", nl: "nl-NL", pl: "pl-PL", ru: "ru-RU",
  };
  return valor.toLocaleString(etiquetas[idioma], {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** El separador de miles del idioma, para que 1.472 no salga siempre a la espanola. */
export function numero(idioma: Idioma, valor: number): string {
  const etiquetas: Record<Idioma, string> = {
    es: "es-ES", de: "de-DE", en: "en-GB", ca: "ca-ES", fr: "fr-FR",
    it: "it-IT", nl: "nl-NL", pl: "pl-PL", ru: "ru-RU",
  };
  return valor.toLocaleString(etiquetas[idioma]);
}
